export type Locale = 'en' | 'ja' | 'de'

export type Messages = Record<string, unknown>

export const messages: Record<Locale, Messages> = {
  en: {
    common: {
      back: 'Back',
      prev: 'Prev',
      next: 'Next',
      close: 'Close',
      cancel: 'Cancel',
      clear: 'Clear',
      continue: 'Continue',
      done: 'Done',
      pageIndicator: 'Page {page} / {total}',
      save: 'Save',
      leaveUnchanged: '(leave unchanged)',
      loading: 'Loading…',
      total: 'Total',
      successful: 'Successful',
      failed: 'Failed',
      upload: 'Upload',
      retry: 'Retry',
      redo: 'Redo',
      reset: 'Reset',
      skip: 'Skip',
      undo: 'Undo',
      import: 'Import',
      deck: 'Deck',
      pause: 'Pause',
      play: 'Play',
      rewind10: 'Rewind 10 seconds',
      forward10: 'Forward 10 seconds',
    },
    deckTemplateEditor: {
      title: 'Deck Settings: {deckTitle}',
      noDeckLoaded: 'No deck loaded.',
      unset: '(unset)',
      sections: {
        size: 'Size',
        colors: 'Colors',
        border: 'Border',
        widgets: 'Widgets',
        shadow: 'Shadow',
        audio: 'Audio',
        motion: 'Transitions + Flip',
      },
      fields: {
        widthPx: 'Width (px)',
        heightPx: 'Height (px)',
        darkMode: 'Dark mode',
        background: 'Background',
        themeBackgroundOverride: 'Theme background (override)',
        borderColor: 'Border color',
        themeBorderColorOverride: 'Theme border color (override)',
        borderWidthPx: 'Border width (px)',
        borderRadiusPx: 'Border radius (px)',
        themeBorderRadiusOverride: 'Theme border radius (override)',
        shadowCss: 'Shadow (CSS box-shadow)',
        shadowColor: 'Shadow color',
        shadowX: 'X',
        shadowY: 'Y',
        shadowBlur: 'Blur',
        shadowSpread: 'Spread',
        autoplay: 'Autoplay',
        clickToPlay: 'Click to play',
        audioNotVisible: 'Not visible (autoplay only)',
        audioForceVisible: 'Force visible audio buttons',
        flowchartHideAudio: 'Hide audio buttons in flowchart mode',
        transitionDurationMs: 'Transition duration (ms)',
        themeTransitionDurationOverride: 'Theme transition duration (override)',
        cardChangeAnimation: 'Card change animation',
        successCardChangeAnimation: 'Success animation',
        failedCardChangeAnimation: 'Failed animation',
        flipAnimation: 'Flip animation',
        themeFlipAnimationOverride: 'Theme flip animation (override)',
        hoverFlip: 'Hover flip',
        showBackFirst: 'Show back first',

        checkboxTickStyle: 'Checkbox mark style',
      },
      tickStyles: {
        fill: 'Filled square (current)',
        x: 'X',
        haken: 'Checkmark (Haken)',
      },
      cardChangeAnimations: {
        none: 'None (instant)',
        fade: 'Fade',
        flip: 'Flip (next card on back)',
        'slide-left': 'Swipe left',
        'slide-right': 'Swipe right',
        'swipe-down': 'Swipe down',
        'reveal-under': 'Reveal under (down-left)',
      },
      preview: {
        aria: 'Preview',
        title: 'Preview',
        emptyCard: 'Empty card',
      },
    },
    elements: {
      image: {
        loadError: 'Image not found.',
      },
    },
    settings: {
      title: 'Settings',
      subtitle: 'Stored locally in this browser.',
      appearanceTitle: 'Appearance',
      behaviorTitle: 'Behavior',
      mediaTitle: 'Media',
      forceDarkModeLabel: 'Force dark mode',
      forceDarkModeHelp: 'Always render flashcards in dark mode on this device.',
      autoFlipLabel: 'Auto-flip back',
      autoFlipHelp: 'After you flip a card, automatically return to the initial face.',
      autoplayAudioLabel: 'Autoplay audio',
      autoplayAudioHelp: 'Try to autoplay audio elements when a card is shown (browser may block).',
      dangerTitle: 'Danger zone',
      dangerHelp: 'This only deletes local browser data (preferences, locale, editor settings).',
      deleteAllData: 'Delete all my local data',
      deleteConfirm: 'Delete all Perlingo local data from this browser?',
      deleteConfirm2: 'This cannot be undone. Continue?',
    },
    deckPlayer: {
      shell: {
        unsupportedModel: 'This learning model is not supported yet.',
        unauthorized: 'You are not authorized to view this content.',
        loginHint: "Not logged in — progress won't be saved.",
        flowStart: 'Start',
        flowPhaseCompleted: 'Well done!',
        flowContinue: 'Continue',
        answerKnown: 'I knew it',
        answerUnknown: "Didn't know",
        flowReset: 'Reset Deck Progress',
        flowResetConfirm: 'Are you really sure?',
        flowPropagationBlocked: 'You can continue tomorrow!',
      },
      host: {
        loadingDeck: 'Loading…',
        showMissingCards: 'Show missing cards',
        showDetails: 'Show details',
        hideDetails: 'Hide details',
        copyDebugInfo: 'Copy debug info',
        copy: 'Copy',
        copied: 'Copied',
        copyFailed: 'Copy failed: {message}',
        missingCardsHeader: 'Missing cards ({count})',
        untitled: '(untitled)',
        titlesLoadedForFirst: '(Titles loaded for first {count} cards)',
        skipButton: 'Skipknopf — jump to the next node',
      },
    },
    editor: {
      faces: {
        front: 'Front',
        back: 'Back',
      },
      elementTypes: {
        text: 'Text',
        image: 'Image',
        'checkbox-group': 'Checklist',
        audio: 'Audio',
        video: 'Video',
        shape: 'Shape',
      },
      elementDescriptions: {
        text: 'Add headings, sub-copy, or prompts.',
        image: 'Drop in artwork, screenshots, or diagrams.',
        'checkbox-group': 'Add quick checkboxes to interact with.',
        audio: 'Play audio clips.',
        video: 'Embed playable videos.',
        shape: 'Rectangle/ellipse blocks for backgrounds and highlights.',
      },
      colorPicker: {
        colorPickerAria: 'Color picker',
        alphaAria: 'Alpha',
      },
      elementEditors: {
        text: {
          labels: {
            text: 'Text',
            fontFamily: 'Font family',
            fontSizePx: 'Font size (px)',
            lineHeight: 'Line height',
            fontWeight: 'Font weight',
            textColor: 'Text color',
          },
        },
        image: {
          labels: {
            uploadImage: 'Upload image',
            imageUrl: 'Image URL',
            altText: 'Alt text',
            sizePercent: 'Size (%)',
            aspectRatio: 'Aspect ratio',
            rotationDeg: 'Rotation (deg)',
            opacity: 'Opacity',
            skewXDeg: 'Skew X (deg)',
            skewYDeg: 'Skew Y (deg)',
          },
          buttons: {
            wpChoose: 'Choose from WP Media…',
            wpReplace: 'Replace via WP Media…',
            remove: 'Remove',
          },
          aria: {
            selectedImagePreview: 'Selected image preview',
            selectedImageAltFallback: 'Selected image',
          },
          toggles: {
            forceDarkModeStyling: 'Force dark mode styling',
            mirrored: 'Mirrored',
          },
          wpMedia: {
            titleSelect: 'Select image',
            titleReplace: 'Replace image',
            buttonUseThis: 'Use this image',
          },
          redo: 'やり直す',
          reset: 'リセット',
          skip: 'スキップ',
        },
        checkboxGroup: {
          labels: {
            count: 'Count',
            direction: 'Direction',
            boxSize: 'Box size',
            gap: 'Gap',
            backgroundColor: 'Background color',
            tickStyle: 'Checkbox mark style',
            values: 'Values',
          },
          options: {
            vertical: 'Vertical',
            horizontal: 'Horizontal',
          },
        },
        audio: {
          labels: {
            uploadAudio: 'Upload audio',
            audioUrl: 'Audio URL',
            sizePercent: 'Size (%)',
            iconSizePx: 'Icon size (px)',
            preload: 'Preload',
            skewXDeg: 'Skew X (deg)',
            skewYDeg: 'Skew Y (deg)',
          },
          buttons: {
            wpChoose: 'Choose from WP Media…',
            wpReplace: 'Replace via WP Media…',
          },
          options: {
            preload: {
              none: 'None',
              metadata: 'Metadata',
              auto: 'Auto',
            },
          },
          toggles: {
            autoplay: 'Autoplay',
            notVisible: 'Not visible (autoplay only)',
            loop: 'Loop',
            muted: 'Muted',
          },
          wpMedia: {
            titleSelect: 'Select audio',
            titleReplace: 'Replace audio',
            buttonUseThis: 'Use this audio',
          },
        },
        video: {
          labels: {
            uploadVideo: 'Upload video',
            videoUrl: 'Video URL',
            posterUrl: 'Poster URL',
            sizePercent: 'Size (%)',
            iconSizePx: 'Icon size (px)',
            aspectRatio: 'Aspect ratio',
            skewXDeg: 'Skew X (deg)',
            skewYDeg: 'Skew Y (deg)',
          },
          toggles: {
            autoplay: 'Autoplay',
            loop: 'Loop',
            muted: 'Muted',
          },
        },
        shape: {
          labels: {
            kind: 'Kind',
            cornerRadius: 'Corner radius',
            widthPercent: 'Width (%)',
            heightPercent: 'Height (%)',
            fill: 'Fill',
            stroke: 'Stroke',
            strokeWidth: 'Stroke width',
            skewXDeg: 'Skew X (deg)',
            skewYDeg: 'Skew Y (deg)',
          },
          options: {
            kind: {
              rectangle: 'Rectangle',
              ellipse: 'Ellipse',
            },
          },
        },
      },
      toolbox: {
        toolboxAria: 'Toolbox',
        inspectorAria: 'Inspector',
        clipboardAria: 'Clipboard actions',
        editorActionsAria: 'Editor actions',
        cardProps: 'Card properties',
        elementProps: 'Element properties',
        canvasSettings: 'Canvas settings',
        previewProps: 'Preview properties',
        shortcuts: 'Keyboard shortcuts',
        audioOrder: 'Audio order',
        duplicate: 'Duplicate',
        copy: 'Copy',
        cut: 'Cut',
        paste: 'Paste',
        delete: 'Delete',
        clearSelection: 'Clear selection',

        status: {
          unsaved: 'Unsaved changes',
          savedAt: 'Saved {time}',
          autosavedAt: 'Autosaved {time}',
        },
      },
      notifications: {
        uploadMissingContext: 'Upload unavailable: missing WordPress REST context (nonce/root).',
        uploadFailed: 'Upload failed: {message}',
        blobNotAllowed: 'blob: URLs are not allowed. Please upload to WordPress or use a real URL.',
      },
      mediaSearch: {
        selectExisting: 'Select existing media',
        placeholder: 'Search by title or ID…',
        searching: 'Searching…',
        typeToSearch: 'Type to search.',
        noResults: 'No results.',
        untitled: 'Untitled',
        used: 'Used',
        playPreview: 'Play',
        stopPreview: 'Stop',
        previewFailed: 'Preview failed: {message}',
        searchFailed: 'Search failed: {message}',
      },
      historyPanel: {
        title: 'History',
        actionsAria: 'History actions',
        undo: 'Undo',
        redo: 'Redo',
        addCheckpoint: 'Add checkpoint',
      },
      layersPanel: {
        title: 'Layers',
        actionsAria: 'Layer actions',
        lockLayer: 'Lock layer',
        unlockLayer: 'Unlock layer',
        moveUp: 'Move layer up',
        moveDown: 'Move layer down',
        deleteLayer: 'Delete layer',
        empty: 'No elements on this face yet.',
        textFallback: 'Text',
      },
      canvasSettings: {
        title: 'Canvas Settings',
        grid: 'Grid',
        gridSize: 'Grid size (px)',
        gridSizeHelp: '{size}px · also used for snapping',
        snapStrength: 'Snap strength',
        snapStrengthHelp: '{pct}% · 0% off, 100% hard snap',
        measurementUnits: 'Measurement units',
        developerSettings: 'Developer Settings',
        units: {
          px: 'Pixels',
          rem: 'rems',
          percent: 'Percent',
        },
        showWarningOverlays: 'Show warning overlays',
      },
      elementProps: {
        title: 'Element Properties',
        noneSelected: 'Select an element to edit its properties.',
        selectedSummary: '{type} element · {face} face',
        multipleSelection: "Multiple selection detected. Bulk editing isn't available yet.",
        sections: {
          general: 'General',
          transform: 'Transform',
        },
        layerTitle: 'Layer title',
        locked: 'Locked',
        positionX: 'Position X (px)',
        positionY: 'Position Y (px)',
        storedRatios: 'Stored as ratios: X {x}, Y {y}',
        noEditableProps: 'This element type does not yet expose editable properties.',
      },
      audioOrder: {
        title: 'Audio Order',
        noAudio: 'No audio elements on this card.',
        front: 'Front',
        back: 'Back',
        indexLabel: 'Index {index}',
        dragHint: 'Drag to reorder',
      },
      preview: {
        title: 'Preview',
      },
      canvas: {
        faceAria: 'Card face',
        swapFaces: 'Swap front/back elements',
        totalElements: '{count} total element{suffix}',
        emptyTitle: 'No elements on this face yet.',
        emptyHint: 'Use the palette to add text, images, or checklists.',
      },
      previewProps: {
        title: 'Preview Properties',
        subtitle: 'Controls for the preview panel.',
        previewControls: 'Preview controls',
        options: 'Options',
        showFront: 'Show front',
        showBack: 'Show back',
        toggle: 'Toggle',
        reset: 'Reset',
        cycleAnimation: 'Cycle animation',
        darkMode: 'Dark mode',
        hoverFlip: 'Hover flip',
        flipAnimation: 'Flip animation',
        flipAnimations: {
          'rotate-y': 'Rotate Y',
          'rotate-x': 'Rotate X',
          slide: 'Slide',
          fade: 'Fade',
          none: 'None',
        },
      },
      shortcuts: {
        title: 'Keyboard Shortcuts',
        subtitle: 'Quick reference for editing.',
        regionAria: 'Keyboard shortcuts',
        colAction: 'Action',
        colShortcut: 'Shortcut',
        groupFaces: 'Faces',
        groupHistory: 'History',
        groupClipboard: 'Clipboard',
        groupSelection: 'Selection',
        groupMove: 'Move',
        groupDelete: 'Delete',
        toggleFrontBack: 'Toggle front/back',
        undo: 'Undo',
        redo: 'Redo',
        copy: 'Copy',
        cut: 'Cut',
        paste: 'Paste',
        toggleSelection: 'Toggle selection',
        rangeSelectLayers: 'Range select (Layers)',
        marqueeSelect: 'Marquee select',
        marqueeSelectHint: 'Drag empty space',
        nudgeSelection: 'Nudge selection',
        nudgeLarger: 'Nudge (larger step)',
        deleteSelected: 'Delete selected',
      },
      cardProps: {
        title: 'Card Properties',
        general: 'General',
        cardSettings: 'Card settings',
        cardTitle: 'Card title',
        status: 'Status',
        widthPx: 'Width (px)',
        heightPx: 'Height (px)',
        borderRadiusPx: 'Border radius (px)',
        backgroundColor: 'Background color',
        transitionDurationMs: 'Transition duration (ms)',
        checkboxTickStyle: 'Checkbox mark style',
        outline: 'Outline',
        outlineColor: 'Outline color',
        outlineWidthPx: 'Outline width (px)',
        shadow: 'Shadow',
        shadowColor: 'Shadow color',
        shadowX: 'Shadow X',
        shadowY: 'Shadow Y',
        shadowBlur: 'Shadow blur',
        shadowSpread: 'Shadow spread',
        flip: 'Flip',
        flipAnimation: 'Flip animation',
        hoverFlip: 'Hover flip',
        showBackFirst: 'Show back first',
        darkMode: 'Dark mode',
        theme: 'Theme',
        themeHelp: 'Theme overrides are optional.',
        themeBackground: 'Theme background',
        themeBorderColor: 'Theme border color',
        themeBorderRadiusPx: 'Theme border radius (px)',
        themeTransitionDurationMs: 'Theme transition duration (ms)',
        themeFlipAnimation: 'Theme flip animation',
        emptyNoOverride: '(empty = no override)',
        inherit: 'Inherit',
        wpStatus: {
          draft: 'Draft',
          publish: 'Publish',
          private: 'Private',
          pending: 'Pending',
        },
      },
      history: {
        initial: 'Initial state',
        load: 'Load',
        save: 'Save',
        autosave: 'Autosave',
        checkpoint: 'Checkpoint',
        set: 'Set {key}',
        addElement: 'Add {type} element',
        updateElement: 'Update element',
        deleteElement: 'Delete element',
        setTitle: 'Set title',
        setStatus: 'Set status',
        swapFaces: 'Swap faces',
        pasteElements: 'Paste elements',
        deleteSelected: 'Delete selected elements',
        reorderLayers: 'Reorder layers',
        moveElement: 'Move element',
        moveElements: 'Move elements',
        nudgeElement: 'Nudge element',
        nudgeElements: 'Nudge elements',
        resizeImage: 'Resize image',
        rotateImage: 'Rotate image',
        resizeElement: 'Resize element',
        rotateElement: 'Rotate element',
        confirmSave: 'Are you really sure? This will Save your changes.',
        confirmRevert: 'Discard changes and revert to the last Save?',
        pastedImageAlt: 'Pasted image',
        toggleLock: 'Toggle lock',
        updateText: 'Update text',
        updateFontFamily: 'Update font family',
        updateFontSize: 'Update font size',
        updateLineHeight: 'Update line height',
        updateFontWeight: 'Update font weight',
        updateTextColor: 'Update text color',
        setImage: 'Set image',
        removeImage: 'Remove image',
        updateImageSrc: 'Update image URL',
        updateImageAlt: 'Update image alt text',
        updateAspectRatio: 'Update aspect ratio',
        toggleDarkModeOverride: 'Toggle dark mode styling',
        toggleMirrored: 'Toggle mirrored',
        updateRotation: 'Update rotation',
        updateOpacity: 'Update opacity',
        updateSkewX: 'Update skew X',
        updateSkewY: 'Update skew Y',
        updateCheckboxCount: 'Update checkbox count',
        toggleCheckbox: 'Toggle checkbox',
        updateDirection: 'Update direction',
        updateBoxSize: 'Update box size',
        updateGap: 'Update gap',
        updateCheckboxTickStyle: 'Update checkbox mark style',
        updateBackgroundColor: 'Update background color',
        setAudio: 'Set audio',
        updateAudioSrc: 'Update audio URL',
        updateAudioSize: 'Update audio size',
        updateAudioIconSize: 'Update audio icon size',
        updatePreload: 'Update preload',
        toggleAutoplay: 'Toggle autoplay',
        toggleNotVisible: 'Toggle not visible',
        toggleLoop: 'Toggle loop',
        toggleMuted: 'Toggle muted',
        setVideo: 'Set video',
        updateVideoSrc: 'Update video URL',
        updateVideoSize: 'Update video size',
        updateVideoIconSize: 'Update video icon size',
        updatePoster: 'Update poster',
        updateShapeKind: 'Update shape kind',
        updateRadius: 'Update radius',
        updateWidth: 'Update width',
        updateHeight: 'Update height',
        updateFill: 'Update fill',
        updateStroke: 'Update stroke',
        updateStrokeWidth: 'Update stroke width',
        renameLayer: 'Rename layer',
        updatePosition: 'Update position',
        updateTitle: 'Update title',
        toggleLockState: 'Toggle lock state',
        reorderAudio: 'Reorder audio elements',
      },
    },
    import: {
      wizard: {
        stepsAria: 'Import steps',
      },
      chooseType: {
        title: 'Choose import type',
        pdf: { title: 'PDF Import' },
        json: { title: 'JSON Import' },
        continuePdf: 'Continue with PDF',
        continueJson: 'Continue with JSON',
        scopeLabel: 'What to import',
        scope: {
          cards: 'Cards',
          decks: 'Decks',
          courses: 'Courses',
          all: 'All',
        },
      },
      sides: {
        label: 'Flashcard sides',
        one: 'One side flashcards import',
        two: 'Two sides flashcards import',
        oneShort: '1 side',
        twoShort: '2 sides',
        front: 'Front',
        back: 'Back',
        frontPdf: 'Front PDF',
        backPdf: 'Back PDF',
        pages: '{count} pages',
        frontStart: 'First front page',
        backStart: 'First back page',
        pagePairs: 'Page pairs to import',
        pairingHint: 'Cards are paired by page and position, left to right and top to bottom.',
        rangeSummary:
          'Front pages {frontStart}–{frontEnd} ↔ back pages {backStart}–{backEnd}. Pages outside these ranges are not imported.',
        invalidRange: 'Choose valid page ranges of the same length in both PDFs.',
        preview: 'Preview side',
        sharedGrid: 'Both sides use the same grid. Excluding a card excludes both sides.',
        loadError: 'Could not load the PDF. Please select a valid PDF file and try again.',
        cutError: 'Could not prepare both sides. Go back and try again.',
      },
      uploadPdf: {
        title: 'Upload PDF',
        dropTitle: 'Drop PDF here',
        dropHint: 'or click to select a file',
        meta: 'PDF only · max size depends on browser',
      },
      recognition: {
        title: 'Card detection',
        ai: 'AI',
        algorithm: 'Algorithm',
        aiHint: 'Codex reads each page (up to 10 pages per request).',
        algorithmHint: 'Reads the PDF text layer, as before.',
        checking: 'Checking Codex…',
        running: 'Codex is reading {done}/{total} page groups…',
        unavailable: 'AI detection unavailable: {reason} The algorithm is used.',
        failed: 'AI detection failed: {reason} The algorithm was used instead.',
        noContext: 'Missing WordPress admin context.',
        notConfigured: 'it is not set up (Settings → Developer).',
        timeout: 'Codex did not answer in time.',
        incomplete: 'Codex did not return every card.',
      },
      configureGrid: {
        title: 'Grid Configuration',
        columns: 'Columns',
        rows: 'Rows',
        gridWidthMm: 'Grid Width (mm)',
        gridHeightMm: 'Grid Height (mm)',
        offsetXmm: 'Offset X (mm)',
        offsetYmm: 'Offset Y (mm)',
        paddingLeftMm: 'Padding Left (mm, inside each card)',
        paddingRightMm: 'Padding Right (mm, inside each card)',
        paddingTopMm: 'Padding Top (mm, inside each card)',
        paddingBottomMm: 'Padding Bottom (mm, inside each card)',
      },
      reviewCuts: {
        cutPreviewAlt: 'Cut preview',
        excluded: 'Excluded',
        processingCuts: 'Cutting cards…',
        deckConfigTitle: 'Deck configuration',
        deckTitle: 'Deck title',
        deckTitlePlaceholder: 'My deck',
        cardIdentifier: 'Card identifier (prefix)',
        cardIdentifierPlaceholder: 'e.g. Vorkurs',
        cardIdentifierHint: 'Used as a prefix: “Identifier - Card name”.',
        cardWidthPx: 'Card width (px)',
        cardHeightPx: 'Card height (px)',
        darkMode: 'Dark mode',
        background: 'Background',
        borderColor: 'Border color',
        borderRadiusPx: 'Border radius (px)',
        transitionMs: 'Transition (ms)',
        flipAnimation: 'Flip animation',
        flipOnHover: 'Flip on hover',
        showBackFirst: 'Show back first',
        themeOverrides: 'Theme overrides (optional)',
        themeBackground: 'Theme background',
        themeBorderColor: 'Theme border color',
        themeRadiusPx: 'Theme radius (px)',
        themeTransitionMs: 'Theme transition (ms)',
        themeFlipAnimation: 'Theme flip animation',
        emptyNoOverride: '(empty = no override)',
        deckTitleRequired: 'Deck title is required before uploading.',
      },
      audioMatch: {
        title: 'Audio matching',
        empty: 'No cards to match (all excluded).',
        untitled: '(untitled)',
        selected: '(selected)',
        noMatch: 'No match',
        multipleCandidates: '{count} matches',
        summary: {
          matched: '{count} matched',
          multiple: '{count} need attention',
          missing: '{count} missing',
        },
        buttons: {
          autoMatch: 'Auto-match',
          forceRematch: 'Force re-match',
          clear: 'Clear selection',
        },
        modal: {
          title: 'Match audio for card',
          current: 'Current audio',
          uploadNew: 'Upload new audio',
          chooseExisting: 'Choose existing audio',
          searchPlaceholder: 'Search by title or ID…',
          typeToSearch: 'Type to search…',
          noResults: 'No results.',
        },
        errors: {
          missingWp: 'Missing WordPress REST context (root/nonce).',
          searchFailed: 'Search failed.',
          uploadFailed: 'Upload failed.',
        },
      },
      upload: {
        title: 'Upload',
        aboutToUpload: 'You are about to upload {count} cards.',
        uploading: 'Uploading… {pct}%',
        confirmHint:
          'This will create media + cards in WordPress. You can retry failures and undo the import.',
        completeFinalized: 'Upload complete. Deck is finalized.',
        finishedWithFailures: 'Upload finished with failures.',
        finished: 'Upload finished.',
        showFailedItems: 'Show failed items',
        unknownError: 'Unknown error',
        undoComplete: 'Undo complete. Created resources were deleted.',
        uploadAgain: 'You can go back and upload again.',
        retryFailed: 'Retry failed',
        undoUpload: 'Undo upload',
        backToReview: 'Back to review',
      },
    },
    tools: {
      tabs: {
        general: 'General',
        students: 'Tools',
      },
      users: {
        intro:
          'Manage user accounts: course access, coins, progress and passwords — per user or in bulk. The “General” group contains all accounts that were not generated (administrators, regular registrations).',
        generalGroup: 'General',
        adminBadge: 'Admin',
        bulkTitle: '{count} selected',
        bulkGrant: 'Grant course',
        bulkRevoke: 'Revoke course',
        bulkAddCoins: 'Add coins',
        bulkSetCoins: 'Set coins',
        bulkAddPearls: 'Add pearls',
        bulkSetPearls: 'Set pearls',
        bulkResetProgress: 'Reset progress',
        bulkResetPasswords: 'Reset passwords',
        bulkDelete: 'Delete',
        courseSelect: 'Select course…',
        allCourses: 'All courses',
        coins: 'Coins',
        pearls: 'Pearls',
        access: 'Access',
        coursesColumn: 'Courses',
        progressTitle: 'Courses & progress',
        completeCourse: 'Complete',
        resetCourse: 'Reset',
        levelsDone: '{done}/{total} levels',
        free: 'free',
        details: 'Details',
        updatedNotice: '{count} user(s) updated.',
        resetProgressConfirm: 'Reset progress for {count} user(s)?',
        resetPwConfirm: 'Generate new passwords for {count} user(s)?',
        errorAction: 'Action failed.',
        needCourse: 'Select a course first.',
      },
      students: {
        intro:
          'Generate anonymous student accounts. Usernames are random and contain no personal data; the group only organizes accounts (e.g. by school or class).',
        accessTitle: 'Course access',
        accessHint: 'Newly generated accounts get these courses unlocked right away.',
        manageHint: 'Existing accounts are managed in the “General” tab.',
        grantFailed: 'Accounts were created, but granting course access failed.',
        groupTitle: 'Group',
        newGroup: 'New group',
        newGroupPlaceholder: 'e.g. schule-linz',
        accountsCount: '{count} accounts',
        generateTitle: 'Generate accounts',
        countLabel: 'How many accounts?',
        generate: 'Generate',
        generating: 'Generating…',
        listTitle: 'Accounts for “{group}”',
        loading: 'Loading…',
        empty: 'No accounts in this group yet.',
        username: 'Username',
        password: 'Password',
        registered: 'Created',
        actions: 'Actions',
        resetPassword: 'Reset password',
        resetConfirm: 'Generate a new password for {username}?',
        delete: 'Delete',
        deleteSelected: 'Delete selected ({count})',
        deleteConfirm: 'Delete {count} student account(s)? This cannot be undone.',
        selectAll: 'Select all',
        deselectAll: 'Deselect all',
        newCredentialsTitle: 'New credentials',
        newCredentialsHint:
          'Passwords are shown only once. Print them now — they cannot be displayed again later.',
        print: 'Print credentials',
        clearCredentials: 'Clear list',
        printSheetTitle: 'Login credentials',
        printSite: 'Website',
        generatedNotice: '{count} account(s) created.',
        deletedNotice: '{count} account(s) deleted.',
        errorLoad: 'Could not load data.',
        errorGenerate: 'Generating accounts failed.',
        errorDelete: 'Deleting failed.',
        errorReset: 'Password reset failed.',
        noContext: 'WordPress context not found. Open this page in wp-admin.',
      },
      audio: {
        overlayTitle: 'Reset audio',
        intro:
          'Bulk-fix card audio: select cards in the Cards list, choose “Reset audio” from Bulk actions, then review and apply matches here — or load specific card IDs below.',
        idsPlaceholder: 'Card IDs, e.g. 12, 45, 78',
        load: 'Load',
        loading: 'Loading…',
        empty:
          'No cards loaded yet. Select cards in the Cards list and choose “Reset audio” from Bulk actions, or load IDs above.',
        groupFilter: 'Group',
        allGroups: 'All groups',
        selectAll: 'Select all',
        matching: 'Matching…',
        rerun: 'Re-run matching',
        summaryMatched: '{count} matched',
        summaryMultiple: '{count} need attention',
        summaryMissing: '{count} missing',
        multipleCandidates: '{count} matches',
        apply: 'Reset audio ({count} selected)',
        applying: 'Applying…',
        applyDone: '{count} card(s) updated.',
        applyPartial: '{done} updated, {failed} failed.',
        currentAudio: 'Current audio',
        noAudio: 'No audio element',
        statusSearching: 'Searching…',
        statusNone: 'No match',
        pickModalTitle: 'Match audio for card',
        currentSelection: 'Current audio',
        clearSelection: 'Clear selection',
        chooseExisting: 'Choose existing audio',
        searchPlaceholder: 'Search media library…',
        noResults: 'No matching audio found.',
        errorLoad: 'Could not load cards.',
        noContext: 'WordPress context not found. Open this page in wp-admin.',
      },
    },
    deckManager: {
      titleFallback: 'Deck',
      deckCardsTitle: 'Deck Cards: {deckTitle}',
      actionsAria: 'Deck actions',
      titlePlaceholder: 'Deck title',
      status: 'Status',
      leaveUnchanged: '(leave unchanged)',
      statusDraft: 'Draft',
      statusPublish: 'Publish',
      statusPrivate: 'Private',
      statusPending: 'Pending',
      allowAnonymous: 'Allow anonymous access',
      allowAnonymousTooltip:
        'When enabled, visitors who are not logged in can view this deck. Off by default.',
      cancel: 'Cancel',
      loadingDeck: 'Loading deck…',
      failedToLoadDeck: 'Failed to load deck.',
      deckCards: 'Deck cards',
      availableCards: 'Available cards',
      total: '{count} total',
      showingCards: 'Showing {showing} of {total} cards',
      searchDeckPlaceholder: 'Search deck cards…',
      searchDeckAria: 'Search deck cards',
      searchAvailablePlaceholder: 'Search available cards…',
      searchAvailableAria: 'Search available cards',
      deckPaginationAria: 'Deck pagination',
      availablePaginationAria: 'Available pagination',
      deckPerPageAria: 'Deck per page',
      availablePerPageAria: 'Available per page',
      sortAria: 'Sort',
      sortOptions: {
        titleAsc: 'Title (A–Z)',
        titleDesc: 'Title (Z–A)',
        deckOrder: 'Deck order',
        newest: 'Newest',
        oldest: 'Oldest',
      },
      perPage: '{count} per page',
      flipCardAria: 'Flip card',
      editCardAria: 'Edit card',
      selectCardAria: 'Select card',
      transferAria: 'Transfer selected cards',
      addAllToDeck: 'Add all available cards to deck',
      removeAllFromDeck: 'Remove all deck cards',
      addSelectedToDeck: 'Add selected to deck',
      removeSelectedFromDeck: 'Remove selected from deck',
      confirmRemoveCards: 'Remove {count} card(s) from the deck?{filtered}',
      confirmAddCards: 'Add {count} card(s) to the deck?{filtered}',
      filteredSuffix: ' (filtered)',
      filteredSearchSuffix: ' (filtered search)',
      saveFailed: 'Save failed',
      emptyDeck: 'No cards yet. Select cards on the right and move them into the deck.',
      noCardsFound: 'No cards found.',
      noCardsOnPage: 'No cards on this page.',
      allCardsAlreadyInDeck: 'All cards are already in the deck.',
    },
    flow: {
      cancel: 'Cancel',
      save: 'Save',
      zoomIn: 'Zoom +',
      zoomOut: 'Zoom -',
      resetView: 'Reset view',
      scale: 'Scale:',
      nodePalette: 'Node Palette',
      nodeSettings: 'Node Settings',
      templates: 'Templates',
      canvasSettings: 'Canvas Settings',
      visualSettings: 'Visual Settings',
      shortcuts: 'Shortcuts',
      nodeLabel: 'Node Label',
      phaseId: 'Phase ID',
      group: 'Group',
      macroName: 'Macro name',
      macroToCall: 'Macro to call',
      leftOperand: 'Left operand',
      leftLiteralValue: 'Left literal value',
      operator: 'Operator',
      rightOperand: 'Right operand',
      rightLiteralValue: 'Right literal value',
      action: 'Action',
      cardSide: 'Card side',
      frontFirst: 'Front first',
      backFirst: 'Back first',
      animation: 'Animation',
      followDeckSetting: 'Follow deck setting',
      slideLeft: 'Slide left',
      slideRight: 'Slide right',
      flip: 'Flip',
      fade: 'Fade',
      swipeUp: 'Swipe up',
      swipeDown: 'Swipe down',
      revealUnder: 'Reveal under',
      scaleAnim: 'Scale',
      audioElementIndex: 'Audio element index',
      waitForAudio: 'Wait for audio to finish before continuing',
      readFrom: 'Read from',
      literalValue: 'Literal value',
      storeInRegister: 'Store in register',
      phaseIdOptional: 'Phase ID (empty = phase of this level)',
      setTarget: 'Set target',
      input: 'Input',
      operation: 'Operation',
      cancelConnectionDrag: 'Cancel active connection drag',
      // Palette
      expandPalette: 'Expand palette',
      collapsePalette: 'Collapse palette',
      expandPanel: 'Expand panel',
      collapsePanel: 'Collapse panel',
      alreadyOnCanvas: 'Already placed on canvas',
      // Node type labels
      nodeType: {
        start: 'Session Start',
        phase: 'Phase Block',
        group: 'Group Gate',
        condition: 'Condition',
        action: 'Action',
        macroStart: 'Macro Start',
        macroEnd: 'Macro End',
        macroCall: 'Macro',
        end: 'Flow End',
      },
      // Node summary strings
      summary: {
        allCards: 'All cards (deck)',
        group: 'Group',
        noGroupSelected: 'No group selected',
        phase: 'Phase',
        noPhaseId: 'No phase ID',
        noAction: 'No action',
        entryPoint: 'Entry point',
        in: 'in',
        out: 'out',
        unnamed: '(unnamed)',
        call: 'Call',
        noMacroSelected: '(no macro selected)',
        terminal: 'Terminal',
      },
      // Inspector / settings
      typeLabel: 'Type',
      allCardsDeck: 'All cards (deck)',
      inputsOutputsSymmetric: 'Inputs / outputs (symmetric handles)',
      outputsInputsSymmetric: 'Outputs / inputs (symmetric handles)',
      selectMacro: '(select macro)',
      inputs: 'input(s)',
      outputs: 'output(s)',
      animNone: 'None (instant)',
      animConfetti: 'Confetti (+ instant change)',
      countN: 'Count (N)',
      delayMs: 'Delay (ms)',
      delayAfterClickMs: 'Delay after click (ms)',
      delayDurationMs: 'Delay duration (ms)',
      targetRegister: 'Target register',
      nLiteralValue: 'N (literal value)',
      operand: 'Operand',
      deleteNode: 'Delete node',
      deleteConnection: 'Delete connection',
      selectedConnection: 'Selected connection',
      selectNodeOrConnection: 'Select a node or connection to edit.',
      // Templates panel
      templateNamePlaceholder: 'Template name…',
      saveCurrent: 'Save current',
      noTemplatesSaved: 'No templates saved yet. Build a flow and save it.',
      doubleClickToRename: 'Double-click to rename',
      addToCanvas: 'Add to canvas at center',
      templateAdd: 'Add',
      templateLoad: 'Load',
      // Canvas settings
      currentScale: 'Current scale',
      infiniteWorld: 'Infinite world centered on all directions',
      canvasView: 'View',
      canvasExport: 'Export',
      downloadSvgTitle: 'Download canvas as SVG vector image',
      downloadAsSvg: 'Download as SVG',
      canvasDebug: 'Debug',
      copyFlowJson: 'Copy flow JSON',
      // Visual settings
      previewDisplay: 'Preview Display',
      disableCardShadows: 'Disable card shadows in preview',
      shadowsDesc: 'By default shadows overflow naturally. Enable this if they are too strong.',
      // Shortcuts panel
      keyboardShortcuts: 'Keyboard Shortcuts',
      shortcutsDesc: 'Quick controls for selection, movement, and graph editing.',
      shortcutsAriaLabel: 'Flow manager shortcut reference',
      shortcutsActionCol: 'Action',
      shortcutsShortcutCol: 'Shortcut',
      shortcutsGroupCanvasTools: 'Canvas Tools',
      shortcutsTogglePanMarquee: 'Toggle Pan / Marquee',
      shortcutsPanHold: 'Pan (hold)',
      shortcutsAlternatePan: 'Alternate pan',
      shortcutsMarqueeSelection: 'Marquee selection (default)',
      shortcutsDrag: 'Drag',
      shortcutsDragOnCanvas: 'Drag on canvas',
      shortcutsGroupCreateNodes: 'Create Nodes (at cursor)',
      shortcutsCreatePhase: 'Phase',
      shortcutsCreateGroupGate: 'Group Gate',
      shortcutsCreatePullCard: 'Pull Card',
      shortcutsCreateFlipCard: 'Flip Card',
      shortcutsCreateDelay: 'Delay',
      shortcutsCreateMathOp: 'Math Op',
      shortcutsCreateCondition: 'Condition',
      shortcutsGroupSelectionMovement: 'Selection & Movement',
      shortcutsToggleNodeInSelection: 'Toggle node in selection',
      shortcutsMoveNodes4px: 'Move selected nodes (4px)',
      shortcutsMoveNodes20px: 'Move selected nodes (20px)',
      shortcutsDeleteSelected: 'Delete selected',
      shortcutsCopySelected: 'Copy selected',
      shortcutsCutSelected: 'Cut selected',
      shortcutsPaste: 'Paste (with internal connections)',
      shortcutsDuplicateSelected: 'Duplicate selected',
      shortcutsClick: 'Click',
      shortcutsGroupConnections: 'Connections',
      shortcutsGroupHistoryFile: 'History & File',
      shortcutsUndo: 'Undo',
      shortcutsRedo: 'Redo',
      shortcutsSaveFlow: 'Save flow',
      // Right rail — Preview
      preview: 'Preview',
      previewPause: 'Pause',
      previewPauseTitle: 'Pause — keep state, step manually',
      previewStop: 'Stop',
      previewStopTitle: 'Stop and reset',
      previewStep: 'Step',
      previewContinue: 'Continue',
      previewContinueTitle: 'Resume auto-play from here',
      previewPlay: 'Play',
      previewPlayTitle: 'Run automatically',
      previewClickCardFirst: 'Click the card first',
      previewAdvanceOneStep: 'Advance one step',
      previewReloadTitle: 'Reload — resumes from saved phase (as a real user would)',
      previewClearSessionTitle:
        'Clear session — wipes saved progress and restarts from the beginning',
      playDelay: 'Play delay',
      clickCardToInteract: 'Click the card to interact',
      stateNode: 'Node',
      statePhase: 'Phase',
      stateGroup: 'Group',
      stateRegisters: 'Registers',
      stateGetters: 'Getters',
      stateSetters: 'Setters',
      previewRequiresWordPress: 'Preview requires the WordPress admin panel.',
      clearingSession: 'Clearing session…',
      sessionCleared: 'Session cleared.',
      clearFailed: 'Clear failed.',
      // History
      history: 'History',
      historyUndo: 'Undo',
      historyRedo: 'Redo',
      historyAddCheckpoint: 'Add checkpoint',
      // Toolbar icon titles
      toolbarDuplicate: 'Duplicate',
      toolbarCopy: 'Copy',
      toolbarCut: 'Cut',
      toolbarPaste: 'Paste',
      toolbarDelete: 'Delete',
      toolbarClearSelection: 'Clear selection',
      toolbarSaveSelectionAsTemplate: 'Save selection as template',
      toolbarPanTool: 'Pan tool',
      toolbarMarqueeTool: 'Marquee tool',
      // Palette items
      palette: {
        categorySession: 'Session',
        categoryMacros: 'Macros',
        categoryStructure: 'Structure',
        categoryCards: 'Cards',
        categoryFlow: 'Flow',
        categoryRegisters: 'Registers',
        categoryPhase: 'Phase',
        categoryMisc: 'Misc',
        sessionStart: 'Session Start',
        sessionStartDesc: 'Entry point of the flow.',
        sessionEnd: 'Session End',
        sessionEndDesc: 'Exit point of the flow.',
        macroStart: 'Macro Start',
        macroStartDesc: 'Named macro entry.',
        macroEnd: 'Macro End',
        macroEndDesc: 'Macro exit point.',
        macroCall: 'Macro',
        macroCallDesc: 'Call a named macro.',
        groupGate: 'Group Gate',
        groupGateDesc: 'Select active group.',
        condition: 'Condition',
        conditionDesc: 'True / else branch.',
        pullCard: 'Pull Card',
        askKnown: 'Ask: known?',
        askKnownDesc: 'Show ✓ / ✗ buttons; continues on the known or unknown output.',
        offerPractice: 'Offer Practice',
        offerPracticeDesc: 'Ask the course to offer the practice round after this level.',
        pullCardDesc: 'Pull next card.',
        clickCheckbox: 'Click Checkbox',
        clickCheckboxDesc: 'Tick N checkboxes.',
        unclickCheckbox: 'Unclick Checkbox',
        unclickCheckboxDesc: 'Untick N checkboxes.',
        flipCard: 'Flip Card',
        flipCardDesc: 'Rotate card front/back.',
        waitClick: 'Wait Click',
        waitClickDesc: 'Wait for user click.',
        delay: 'Delay',
        delayDesc: 'Pause before continuing.',
        earlyExit: 'Early Exit',
        earlyExitDesc: 'Complete flow early.',
        playAudio: 'Play Audio',
        playAudioDesc: 'Play card audio.',
        action: 'Action',
        actionDesc: 'Generic flow action.',
        getValue: 'Get Value',
        getValueDesc: 'Source → register.',
        setValue: 'Set Value',
        setValueDesc: 'Register → target.',
        math: 'Math',
        mathDesc: 'Register arithmetic.',
        phase: 'Phase',
        phaseDesc: 'Checkpoint marker.',
        phaseSave: 'Phase Save',
        phaseSaveDesc: 'Save progress checkpoint.',
        categoryMedia: 'Media',
        showMedia: 'Show Media',
        showMediaDesc: 'Display course media (image, video, audio, or PDF).',
      },
      displayMode: 'Display Mode',
      standalone: 'Standalone',
      standaloneDesc: 'Hide the flashcard; show only the media.',
      modal: 'Modal',
      modalDesc: 'Floating panel on top of the flashcard.',
      split: 'Split Screen',
      splitDesc: 'Show two media items side by side.',
      panel: 'Side Panel',
      displayModePanelHint: 'Media opens as a side panel alongside the flashcard.',
      splitRightMedia: 'Right side (media name)',
      splitRightMediaPlaceholder: 'Media element name…',
      splitSwap: 'Swap left and right',
    },
  },
  ja: {
    // TODO: translate
    common: {
      back: '戻る',
      prev: '前へ',
      next: '次へ',
      close: '閉じる',
      cancel: 'キャンセル',
      clear: 'クリア',
      continue: '続行',
      done: '完了',
      pageIndicator: 'ページ {page} / {total}',
      save: '保存',
      leaveUnchanged: '(変更しない)',
      loading: '読み込み中…',
      total: '合計',
      successful: '成功',
      failed: '失敗',
      upload: 'アップロード',
      retry: '再試行',
      undo: '取り消す',
      import: 'インポート',
      deck: 'デッキ',
      pause: '一時停止',
      play: '再生',
      rewind10: '10秒戻る',
      forward10: '10秒進む',
    },
    deckTemplateEditor: {
      title: 'デッキ設定: {deckTitle}',
      noDeckLoaded: 'デッキが読み込まれていません。',
      unset: '(未設定)',
      sections: {
        size: 'サイズ',
        colors: '色',
        border: '枠線',
        widgets: 'ウィジェット',
        shadow: '影',
        audio: 'オーディオ',
        motion: 'トランジション + 反転',
      },
      fields: {
        widthPx: '幅 (px)',
        heightPx: '高さ (px)',
        darkMode: 'ダークモード',
        background: '背景',
        themeBackgroundOverride: 'テーマ背景（上書き）',
        borderColor: '枠線色',
        themeBorderColorOverride: 'テーマ枠線色（上書き）',
        borderWidthPx: '枠線幅 (px)',
        borderRadiusPx: '角丸 (px)',
        themeBorderRadiusOverride: 'テーマ角丸（上書き）',
        shadowCss: '影（CSS box-shadow）',
        shadowColor: '影の色',
        shadowX: 'X',
        shadowY: 'Y',
        shadowBlur: 'ぼかし',
        shadowSpread: '広がり',
        autoplay: '自動再生',
        clickToPlay: 'クリックで再生',
        audioNotVisible: '非表示 (自動再生のみ)',
        audioForceVisible: '音声ボタンを常に表示',
        flowchartHideAudio: 'フローチャートモードで音声ボタンを非表示',
        transitionDurationMs: 'トランジション時間 (ms)',
        themeTransitionDurationOverride: 'テーマトランジション時間（上書き）',
        cardChangeAnimation: 'カード切り替えアニメーション',
        successCardChangeAnimation: '成功時アニメーション',
        failedCardChangeAnimation: '失敗時アニメーション',
        flipAnimation: '反転アニメーション',
        themeFlipAnimationOverride: 'テーマ反転アニメーション（上書き）',
        hoverFlip: 'ホバーで反転',
        showBackFirst: '裏面を先に表示',

        checkboxTickStyle: 'チェック印のスタイル',
      },
      tickStyles: {
        fill: '塗りつぶし（現在）',
        x: '×',
        haken: 'チェック（ハーケン）',
      },
      cardChangeAnimations: {
        none: 'なし（即時）',
        fade: 'フェード',
        flip: 'フリップ（裏面が次カード）',
        'slide-left': '左へスワイプ',
        'slide-right': '右へスワイプ',
        'swipe-down': '下へスワイプ',
        'reveal-under': '下に表示（左下へ）',
      },
      preview: {
        aria: 'プレビュー',
        title: 'プレビュー',
        emptyCard: '空のカード',
      },
    },
    elements: {
      image: {
        loadError: '画像が見つかりません。',
      },
    },
    settings: {
      title: '設定',
      subtitle: 'このブラウザにローカル保存されます。',
      appearanceTitle: '外観',
      behaviorTitle: '動作',
      mediaTitle: 'メディア',
      forceDarkModeLabel: 'ダークモードを強制',
      forceDarkModeHelp: 'この端末では常にフラッシュカードをダークモードで表示します。',
      autoFlipLabel: '自動で元に戻す',
      autoFlipHelp: 'カードをめくった後、一定時間で最初の面に戻します。',
      buttons: {
        wpChoose: 'WPメディアから選択…',
        wpReplace: 'WPメディアで置換…',
      },
      autoplayAudioLabel: '音声を自動再生',
      autoplayAudioHelp:
        'カード表示時に音声の自動再生を試みます（ブラウザによりブロックされる場合があります）。',
      dangerTitle: '危険ゾーン',
      dangerHelp:
        '削除されるのはローカルのブラウザデータのみです（設定・言語・エディタ設定など）。',
      deleteAllData: 'ローカルデータをすべて削除',
      deleteConfirm: 'このブラウザから Perlingo のローカルデータをすべて削除しますか？',
      deleteConfirm2: 'この操作は元に戻せません。続行しますか？',
    },
    deckPlayer: {
      shell: {
        unsupportedModel: 'この学習モデルはまだサポートされていません。',
        unauthorized: 'このコンテンツを表示する権限がありません。',
        loginHint: 'ログインしていません — 進捗は保存されません。',
        flowStart: 'スタート',
        flowPhaseCompleted: 'よくできました！',
        flowContinue: '続ける',
        answerKnown: '知っていた',
        answerUnknown: '知らなかった',
        flowReset: '進捗をリセット',
        flowResetConfirm: '本当によろしいですか？',
        flowPropagationBlocked: '明日また続けましょう！',
      },
      host: {
        loadingDeck: '読み込み中…',
        showMissingCards: '不足カードを表示',
        showDetails: '詳細を表示',
        hideDetails: '詳細を非表示',
        copyDebugInfo: 'デバッグ情報をコピー',
        copy: 'コピー',
        copied: 'コピーしました',
        copyFailed: 'コピーに失敗しました: {message}',
        missingCardsHeader: '不足カード ({count})',
        untitled: '(無題)',
        titlesLoadedForFirst: '(最初の{count}件のカードのタイトルを読み込みました)',
        skipButton: 'スキップボタン — 次のノードへ進む',
      },
    },
    editor: {
      faces: {
        front: '表',
        back: '裏',
      },
      elementTypes: {
        text: 'テキスト',
        image: '画像',
        'checkbox-group': 'チェックリスト',
        audio: '音声',
        video: '動画',
        shape: '図形',
      },
      elementDescriptions: {
        text: '見出し、説明文、プロンプトを追加します。',
        image: 'イラスト、スクリーンショット、図を追加します。',
        'checkbox-group': '操作できるチェックボックスを追加します。',
        audio: '音声クリップを再生します。',
        video: '再生可能な動画を埋め込みます。',
        shape: '背景や強調表示用の四角形/楕円形ブロックです。',
      },
      colorPicker: {
        colorPickerAria: 'カラーピッカー',
        alphaAria: '透明度',
      },
      elementEditors: {
        text: {
          labels: {
            text: 'テキスト',
            fontFamily: 'フォントファミリー',
            fontSizePx: 'フォントサイズ (px)',
            lineHeight: '行間',
            fontWeight: '太さ',
            textColor: '文字色',
          },
        },
        image: {
          labels: {
            uploadImage: '画像をアップロード',
            imageUrl: '画像URL',
            altText: '代替テキスト',
            sizePercent: 'サイズ (%)',
            aspectRatio: 'アスペクト比',
            rotationDeg: '回転 (度)',
            opacity: '不透明度',
            skewXDeg: '傾き X (度)',
            skewYDeg: '傾き Y (度)',
          },
          buttons: {
            wpChoose: 'WPメディアから選択…',
            wpReplace: 'WPメディアで置換…',
            remove: '削除',
          },
          aria: {
            selectedImagePreview: '選択中の画像プレビュー',
            selectedImageAltFallback: '選択中の画像',
          },
          toggles: {
            forceDarkModeStyling: 'ダークモード用スタイルを強制',
            mirrored: '反転',
          },
          wpMedia: {
            titleSelect: '画像を選択',
            titleReplace: '画像を置換',
            buttonUseThis: 'この画像を使用',
          },
        },
        checkboxGroup: {
          labels: {
            count: '数',
            direction: '方向',
            boxSize: 'ボックスサイズ',
            gap: '間隔',
            backgroundColor: '背景色',
            tickStyle: 'チェック印のスタイル',
            values: '値',
          },
          options: {
            vertical: '縦',
            horizontal: '横',
          },
        },
        audio: {
          labels: {
            uploadAudio: '音声をアップロード',
            audioUrl: '音声URL',
            sizePercent: 'サイズ (%)',
            iconSizePx: 'アイコンサイズ (px)',
            preload: '事前読み込み',
            skewXDeg: '傾き X (度)',
            skewYDeg: '傾き Y (度)',
          },
          options: {
            preload: {
              none: 'なし',
              metadata: 'メタデータ',
              auto: '自動',
            },
          },
          toggles: {
            autoplay: '自動再生',
            notVisible: '非表示 (自動再生のみ)',
            loop: 'ループ',
            muted: 'ミュート',
          },
        },
        video: {
          labels: {
            uploadVideo: '動画をアップロード',
            videoUrl: '動画URL',
            posterUrl: 'ポスターURL',
            sizePercent: 'サイズ (%)',
            iconSizePx: 'アイコンサイズ (px)',
            aspectRatio: 'アスペクト比',
            skewXDeg: '傾き X (度)',
            skewYDeg: '傾き Y (度)',
          },
          toggles: {
            autoplay: '自動再生',
            loop: 'ループ',
            muted: 'ミュート',
          },
        },
        shape: {
          labels: {
            kind: '種類',
            cornerRadius: '角丸',
            widthPercent: '幅 (%)',
            heightPercent: '高さ (%)',
            fill: '塗り',
            stroke: '線',
            strokeWidth: '線幅',
            skewXDeg: '傾き X (度)',
            skewYDeg: '傾き Y (度)',
          },
          options: {
            kind: {
              rectangle: '長方形',
              ellipse: '楕円',
            },
          },
        },
      },
      toolbox: {
        toolboxAria: 'ツールボックス',
        inspectorAria: 'インスペクター',
        clipboardAria: 'クリップボード操作',
        editorActionsAria: 'エディター操作',
        cardProps: 'カード設定',
        elementProps: '要素設定',
        canvasSettings: 'キャンバス設定',
        previewProps: 'プレビュー設定',
        shortcuts: 'キーボードショートカット',
        audioOrder: '音声の順序',
        duplicate: '複製',
        copy: 'コピー',
        cut: '切り取り',
        paste: '貼り付け',
        delete: '削除',
        clearSelection: '選択を解除',

        status: {
          unsaved: '未保存の変更があります',
          savedAt: '{time} に保存',
          autosavedAt: '{time} に自動保存',
        },
      },
      notifications: {
        uploadMissingContext:
          'アップロード不可: WordPressのRESTコンテキスト(Nonce/Root)がありません。',
        uploadFailed: 'アップロードに失敗しました: {message}',
        blobNotAllowed:
          'blob: URLは使用できません。WordPressへアップロードするか、正しいURLを使ってください。',
      },
      mediaSearch: {
        selectExisting: '既存メディアを選択',
        placeholder: 'タイトルまたはIDで検索…',
        searching: '検索中…',
        typeToSearch: '入力して検索します。',
        noResults: '結果がありません。',
        untitled: '無題',
        used: '使用中',
        playPreview: '再生',
        stopPreview: '停止',
        previewFailed: 'プレビューに失敗しました: {message}',
        searchFailed: '検索に失敗しました: {message}',
      },
      historyPanel: {
        title: '履歴',
        actionsAria: '履歴操作',
        undo: '元に戻す',
        redo: 'やり直す',
        addCheckpoint: 'チェックポイント追加',
      },
      layersPanel: {
        title: 'レイヤー',
        actionsAria: 'レイヤー操作',
        lockLayer: 'レイヤーをロック',
        unlockLayer: 'ロック解除',
        moveUp: '上に移動',
        moveDown: '下に移動',
        deleteLayer: 'レイヤーを削除',
        empty: 'この面には要素がまだありません。',
        textFallback: 'テキスト',
      },
      canvasSettings: {
        title: 'キャンバス設定',
        grid: 'グリッド',
        gridSize: 'グリッドサイズ (px)',
        gridSizeHelp: '{size}px · スナップにも使用',
        snapStrength: 'スナップ強度',
        snapStrengthHelp: '{pct}% · 0% オフ, 100% 強スナップ',
        measurementUnits: '単位',
        developerSettings: '開発者設定',
        units: {
          px: 'ピクセル',
          rem: 'rem',
          percent: 'パーセント',
        },
        showWarningOverlays: '警告オーバーレイを表示',
      },
      elementProps: {
        title: '要素設定',
        noneSelected: '要素を選択して編集します。',
        selectedSummary: '{type} · {face}',
        multipleSelection: '複数選択です。まとめて編集はまだ対応していません。',
        sections: {
          general: '一般',
          transform: '変形',
        },
        layerTitle: 'レイヤー名',
        locked: 'ロック',
        positionX: '位置X (px)',
        positionY: '位置Y (px)',
        storedRatios: '比率で保存: X {x}, Y {y}',
        noEditableProps: 'この要素タイプはまだ編集項目がありません。',
      },
      audioOrder: {
        title: '音声の順序',
        noAudio: 'このカードに音声要素がありません。',
        front: '表',
        back: '裏',
        indexLabel: 'インデックス {index}',
        dragHint: 'ドラッグして並べ替え',
      },
      preview: {
        title: 'プレビュー',
      },
      canvas: {
        faceAria: 'カード面',
        swapFaces: '表/裏の要素を入れ替え',
        totalElements: '合計 {count} 件',
        emptyTitle: 'この面には要素がまだありません。',
        emptyHint: 'パレットからテキスト・画像・チェックリストを追加してください。',
      },
      previewProps: {
        title: 'プレビュー設定',
        subtitle: 'プレビューパネルの操作。',
        previewControls: 'プレビュー操作',
        options: 'オプション',
        showFront: '表を表示',
        showBack: '裏を表示',
        toggle: '切り替え',
        reset: 'リセット',
        cycleAnimation: 'アニメーション確認',
        darkMode: 'ダークモード',
        hoverFlip: 'ホバーで反転',
        flipAnimation: '反転アニメーション',
        flipAnimations: {
          'rotate-y': 'Y回転',
          'rotate-x': 'X回転',
          slide: 'スライド',
          fade: 'フェード',
          none: 'なし',
        },
      },
      shortcuts: {
        title: 'キーボードショートカット',
        subtitle: '編集の早見表。',
        regionAria: 'キーボードショートカット',
        colAction: '操作',
        colShortcut: 'キー',
        groupFaces: '面',
        groupHistory: '履歴',
        groupClipboard: 'クリップボード',
        groupSelection: '選択',
        groupMove: '移動',
        groupDelete: '削除',
        toggleFrontBack: '表/裏 切り替え',
        undo: '元に戻す',
        redo: 'やり直す',
        copy: 'コピー',
        cut: '切り取り',
        paste: '貼り付け',
        toggleSelection: '選択の追加/解除',
        rangeSelectLayers: '範囲選択 (レイヤー)',
        marqueeSelect: '矩形選択',
        marqueeSelectHint: '空白をドラッグ',
        nudgeSelection: '微調整移動',
        nudgeLarger: '微調整 (大)',
        deleteSelected: '選択を削除',
      },
      cardProps: {
        title: 'カード設定',
        general: '一般',
        cardSettings: 'カード設定',
        cardTitle: 'カード名',
        status: 'ステータス',
        widthPx: '幅 (px)',
        heightPx: '高さ (px)',
        borderRadiusPx: '角丸 (px)',
        backgroundColor: '背景色',
        transitionDurationMs: 'トランジション時間 (ms)',
        checkboxTickStyle: 'チェック印のスタイル',
        outline: '枠線',
        outlineColor: '枠線色',
        outlineWidthPx: '枠線幅 (px)',
        shadow: '影',
        shadowColor: '影の色',
        shadowX: '影 X',
        shadowY: '影 Y',
        shadowBlur: 'ぼかし',
        shadowSpread: '広がり',
        flip: '反転',
        flipAnimation: '反転アニメーション',
        hoverFlip: 'ホバーで反転',
        showBackFirst: '裏を先に表示',
        darkMode: 'ダークモード',
        theme: 'テーマ',
        themeHelp: 'テーマ上書きは任意です。',
        themeBackground: 'テーマ背景',
        themeBorderColor: 'テーマ枠線色',
        themeBorderRadiusPx: 'テーマ角丸 (px)',
        themeTransitionDurationMs: 'テーマトランジション (ms)',
        themeFlipAnimation: 'テーマ反転アニメーション',
        emptyNoOverride: '(空欄 = 上書きなし)',
        inherit: '継承',
        wpStatus: {
          draft: '下書き',
          publish: '公開',
          private: '非公開',
          pending: '承認待ち',
        },
      },
      history: {
        initial: '初期状態',
        load: '読み込み',
        save: '保存',
        autosave: '自動保存',
        checkpoint: 'チェックポイント',
        set: '{key} を設定',
        addElement: '{type} 要素を追加',
        updateElement: '要素を更新',
        deleteElement: '要素を削除',
        setTitle: 'タイトルを設定',
        setStatus: 'ステータスを設定',
        swapFaces: '表/裏を入れ替え',
        pasteElements: '要素を貼り付け',
        deleteSelected: '選択した要素を削除',
        reorderLayers: 'レイヤー順を変更',
        moveElement: '要素を移動',
        moveElements: '要素を移動',
        nudgeElement: '要素を微調整',
        nudgeElements: '要素を微調整',
        resizeImage: '画像サイズ変更',
        rotateImage: '画像を回転',
        resizeElement: 'サイズ変更',
        rotateElement: '回転',
        confirmSave: '本当に保存しますか？変更が保存されます。',
        confirmRevert: '変更を破棄して直近の保存状態に戻しますか？',
        pastedImageAlt: '貼り付けた画像',
        toggleLock: 'ロック切替',
        updateText: 'テキスト更新',
        updateFontFamily: 'フォントファミリーを更新',
        updateFontSize: 'フォントサイズを更新',
        updateLineHeight: '行間を更新',
        updateFontWeight: '太さを更新',
        updateTextColor: '文字色を更新',
        setImage: '画像を設定',
        removeImage: '画像を削除',
        updateImageSrc: '画像URLを更新',
        updateImageAlt: '代替テキストを更新',
        updateAspectRatio: 'アスペクト比を更新',
        toggleDarkModeOverride: 'ダークモード用スタイル切替',
        toggleMirrored: '反転切替',
        updateRotation: '回転を更新',
        updateOpacity: '不透明度を更新',
        updateSkewX: '傾きXを更新',
        updateSkewY: '傾きYを更新',
        updateCheckboxCount: 'チェック数を更新',
        toggleCheckbox: 'チェック切替',
        updateDirection: '方向を更新',
        updateBoxSize: 'ボックスサイズを更新',
        updateGap: '間隔を更新',
        updateCheckboxTickStyle: 'チェック印のスタイルを更新',
        updateBackgroundColor: '背景色を更新',
        setAudio: '音声を設定',
        updateAudioSrc: '音声URLを更新',
        updateAudioSize: '音声サイズを更新',
        updateAudioIconSize: '音声アイコンサイズを更新',
        updatePreload: '事前読み込みを更新',
        toggleAutoplay: '自動再生切替',
        toggleNotVisible: '非表示切替',
        toggleLoop: 'ループ切替',
        toggleMuted: 'ミュート切替',
        setVideo: '動画を設定',
        updateVideoSrc: '動画URLを更新',
        updateVideoSize: '動画サイズを更新',
        updateVideoIconSize: '動画アイコンサイズを更新',
        updatePoster: 'ポスターを更新',
        updateShapeKind: '図形の種類を更新',
        updateRadius: '角丸を更新',
        updateWidth: '幅を更新',
        updateHeight: '高さを更新',
        updateFill: '塗りを更新',
        updateStroke: '線を更新',
        updateStrokeWidth: '線幅を更新',
        renameLayer: 'レイヤー名変更',
        updatePosition: '位置を更新',
        updateTitle: 'タイトルを更新',
        toggleLockState: 'ロック状態切替',
        reorderAudio: '音声要素を並べ替え',
      },
    },
    import: {
      wizard: {
        stepsAria: 'インポート手順',
      },
      chooseType: {
        title: 'インポート種別を選択',
        pdf: { title: 'PDFインポート' },
        json: { title: 'JSONインポート' },
        continuePdf: 'PDFで続行',
        continueJson: 'JSONで続行',
        scopeLabel: 'インポート対象',
        scope: {
          cards: 'カード',
          decks: 'スタック',
          courses: 'コース',
          all: 'すべて',
        },
      },
      sides: {
        label: 'カードの面',
        one: '片面フラッシュカードのインポート',
        two: '両面フラッシュカードのインポート',
        oneShort: '片面',
        twoShort: '両面',
        front: '表面',
        back: '裏面',
        frontPdf: '表面のPDF',
        backPdf: '裏面のPDF',
        pages: '{count}ページ',
        frontStart: '表面の開始ページ',
        backStart: '裏面の開始ページ',
        pagePairs: 'インポートするページ数',
        pairingHint: 'ページと位置に従い、左から右、上から下の順でカードを組み合わせます。',
        rangeSummary:
          '表面 {frontStart}–{frontEnd}ページ ↔ 裏面 {backStart}–{backEnd}ページ。範囲外のページはインポートされません。',
        invalidRange: '両方のPDFから同じ長さの有効なページ範囲を選択してください。',
        preview: '表示する面',
        sharedGrid: '両面に同じグリッドを使用します。除外すると両面が除外されます。',
        loadError: 'PDFを読み込めませんでした。有効なPDFファイルを選択して再試行してください。',
        cutError: '両面を準備できませんでした。戻って再試行してください。',
      },
      uploadPdf: {
        title: 'PDFをアップロード',
        dropTitle: 'ここにPDFをドロップ',
        dropHint: 'またはクリックして選択',
        meta: 'PDFのみ・最大サイズはブラウザ依存',
      },
      recognition: {
        title: 'カード認識',
        ai: 'AI',
        algorithm: 'アルゴリズム',
        aiHint: 'Codexが各ページを読み取ります（1回のリクエストで最大10ページ）。',
        algorithmHint: 'これまでどおりPDFのテキストレイヤーを読み取ります。',
        checking: 'Codexを確認中…',
        running: 'Codexがページグループを読み取り中 {done}/{total}…',
        unavailable: 'AI認識は利用できません：{reason} アルゴリズムを使用します。',
        failed: 'AI認識に失敗しました：{reason} 代わりにアルゴリズムを使用しました。',
        noContext: 'WordPress管理画面のコンテキストがありません。',
        notConfigured: '設定されていません（設定 → 開発者向け）。',
        timeout: 'Codexが時間内に応答しませんでした。',
        incomplete: 'Codexがすべてのカードを返しませんでした。',
      },
      configureGrid: {
        title: 'グリッド設定',
        columns: '列',
        rows: '行',
        gridWidthMm: 'グリッド幅 (mm)',
        gridHeightMm: 'グリッド高さ (mm)',
        offsetXmm: 'オフセットX (mm)',
        offsetYmm: 'オフセットY (mm)',
        paddingLeftMm: '左余白 (mm, カード内)',
        paddingRightMm: '右余白 (mm, カード内)',
        paddingTopMm: '上余白 (mm, カード内)',
        paddingBottomMm: '下余白 (mm, カード内)',
      },
      reviewCuts: {
        cutPreviewAlt: '切り抜きプレビュー',
        excluded: '除外',
        processingCuts: 'カードを切り抜いています…',
        deckConfigTitle: 'デッキ設定',
        deckTitle: 'デッキ名',
        deckTitlePlaceholder: '例: My deck',
        cardIdentifier: 'カード識別子 (接頭辞)',
        cardIdentifierPlaceholder: '例: Vorkurs',
        cardIdentifierHint: '“識別子 - カード名” の接頭辞として使われます。',
        cardWidthPx: 'カード幅 (px)',
        cardHeightPx: 'カード高さ (px)',
        darkMode: 'ダークモード',
        background: '背景',
        borderColor: '枠線色',
        borderRadiusPx: '角丸 (px)',
        transitionMs: 'トランジション (ms)',
        flipAnimation: '反転アニメーション',
        flipOnHover: 'ホバーで反転',
        showBackFirst: '裏面を先に表示',
        themeOverrides: 'テーマ上書き (任意)',
        themeBackground: 'テーマ背景',
        themeBorderColor: 'テーマ枠線色',
        themeRadiusPx: 'テーマ角丸 (px)',
        themeTransitionMs: 'テーマトランジション (ms)',
        themeFlipAnimation: 'テーマ反転アニメーション',
        emptyNoOverride: '(空欄 = 上書きなし)',
        deckTitleRequired: 'アップロード前にデッキ名が必要です。',
      },
      audioMatch: {
        title: '音声の一致',
        empty: '一致させるカードがありません（すべて除外済み）。',
        untitled: '(無題)',
        selected: '(選択済み)',
        noMatch: '一致なし',
        multipleCandidates: '{count} 件の一致',
        summary: {
          matched: '{count} 件一致',
          multiple: '{count} 件要確認',
          missing: '{count} 件不足',
        },
        buttons: {
          autoMatch: '自動一致',
          forceRematch: '強制再一致',
          clear: '選択をクリア',
        },
        modal: {
          title: 'カードの音声を設定',
          current: '現在の音声',
          uploadNew: '新しい音声をアップロード',
          chooseExisting: '既存の音声を選択',
          searchPlaceholder: 'タイトルまたはIDで検索…',
          typeToSearch: '検索語を入力…',
          noResults: '結果がありません。',
        },
        errors: {
          missingWp: 'WordPress RESTの情報（root/nonce）が見つかりません。',
          searchFailed: '検索に失敗しました。',
          uploadFailed: 'アップロードに失敗しました。',
        },
      },
      upload: {
        title: 'アップロード',
        aboutToUpload: '{count}枚のカードをアップロードします。',
        uploading: 'アップロード中… {pct}%',
        confirmHint:
          'WordPressにメディアとカードを作成します。失敗は再試行でき、インポートを取り消せます。',
        completeFinalized: 'アップロード完了。デッキは確定しました。',
        finishedWithFailures: '失敗がある状態で完了しました。',
        finished: 'アップロードが完了しました。',
        showFailedItems: '失敗した項目を表示',
        unknownError: '不明なエラー',
        undoComplete: '取り消し完了。作成されたリソースは削除されました。',
        uploadAgain: '戻って再度アップロードできます。',
        retryFailed: '失敗分を再試行',
        undoUpload: 'アップロードを取り消す',
        backToReview: 'レビューに戻る',
      },
    },
    tools: {
      tabs: {
        general: '一般',
        students: 'ツール',
      },
      users: {
        intro:
          'ユーザーアカウントの管理：コースアクセス、コイン、進捗、パスワード — 個別または一括で。「一般」グループには生成されていないアカウント（管理者や通常登録）がすべて含まれます。',
        generalGroup: '一般',
        adminBadge: '管理者',
        bulkTitle: '{count} 件選択中',
        bulkGrant: 'コースを付与',
        bulkRevoke: 'コースを取り消す',
        bulkAddCoins: 'コインを追加',
        bulkSetCoins: 'コインを設定',
        bulkAddPearls: '真珠を追加',
        bulkSetPearls: '真珠を設定',
        bulkResetProgress: '進捗をリセット',
        bulkResetPasswords: 'パスワードをリセット',
        bulkDelete: '削除',
        courseSelect: 'コースを選択…',
        allCourses: 'すべてのコース',
        coins: 'コイン',
        pearls: '真珠',
        access: 'アクセス',
        coursesColumn: 'コース',
        progressTitle: 'コースと進捗',
        completeCourse: '完了にする',
        resetCourse: 'リセット',
        levelsDone: '{done}/{total} レベル',
        free: '無料',
        details: '詳細',
        updatedNotice: '{count} 件のユーザーを更新しました。',
        resetProgressConfirm: '{count} 件のユーザーの進捗をリセットしますか？',
        resetPwConfirm: '{count} 件のユーザーの新しいパスワードを生成しますか？',
        errorAction: '操作に失敗しました。',
        needCourse: '先にコースを選択してください。',
      },
      students: {
        accessTitle: 'コースアクセス',
        accessHint: '新しく生成されたアカウントは、これらのコースがすぐに利用可能になります。',
        manageHint: '既存のアカウントは「一般」タブで管理します。',
        grantFailed: 'アカウントは作成されましたが、コースアクセスの付与に失敗しました。',
        intro:
          '匿名の生徒アカウントを生成します。ユーザー名はランダムで個人情報を含みません。グループはアカウントの整理（学校やクラスなど）にのみ使われます。',
        groupTitle: 'グループ',
        newGroup: '新しいグループ',
        newGroupPlaceholder: '例: schule-linz',
        accountsCount: '{count} 件のアカウント',
        generateTitle: 'アカウントを生成',
        countLabel: 'アカウント数',
        generate: '生成',
        generating: '生成中…',
        listTitle: '「{group}」のアカウント',
        loading: '読み込み中…',
        empty: 'このグループのアカウントはまだありません。',
        username: 'ユーザー名',
        password: 'パスワード',
        registered: '作成日',
        actions: '操作',
        resetPassword: 'パスワードをリセット',
        resetConfirm: '{username} の新しいパスワードを生成しますか？',
        delete: '削除',
        deleteSelected: '選択した項目を削除 ({count})',
        deleteConfirm: '{count} 件の生徒アカウントを削除しますか？元に戻せません。',
        selectAll: 'すべて選択',
        deselectAll: '選択を解除',
        newCredentialsTitle: '新しいログイン情報',
        newCredentialsHint:
          'パスワードは一度しか表示されません。後で再表示できないため、今すぐ印刷してください。',
        print: 'ログイン情報を印刷',
        clearCredentials: 'リストをクリア',
        printSheetTitle: 'ログイン情報',
        printSite: 'ウェブサイト',
        generatedNotice: '{count} 件のアカウントを作成しました。',
        deletedNotice: '{count} 件のアカウントを削除しました。',
        errorLoad: 'データを読み込めませんでした。',
        errorGenerate: 'アカウントの生成に失敗しました。',
        errorDelete: '削除に失敗しました。',
        errorReset: 'パスワードのリセットに失敗しました。',
        noContext:
          'WordPress コンテキストが見つかりません。wp-admin でこのページを開いてください。',
      },
      audio: {
        overlayTitle: '音声をリセット',
        intro:
          'カードの音声を一括修正：カード一覧でカードを選択し、一括操作から「音声をリセット」を選ぶか、下でカードIDを直接読み込んでください。',
        idsPlaceholder: 'カードID（例: 12, 45, 78）',
        load: '読み込み',
        loading: '読み込み中…',
        empty:
          'カードがまだ読み込まれていません。カード一覧でカードを選択し、一括操作から「音声をリセット」を選ぶか、上でIDを読み込んでください。',
        groupFilter: 'グループ',
        allGroups: 'すべてのグループ',
        selectAll: 'すべて選択',
        matching: '照合中…',
        rerun: '再照合',
        summaryMatched: '{count} 件一致',
        summaryMultiple: '{count} 件要確認',
        summaryMissing: '{count} 件未一致',
        multipleCandidates: '{count} 件の候補',
        apply: '音声をリセット（{count} 件選択中）',
        applying: '適用中…',
        applyDone: '{count} 件のカードを更新しました。',
        applyPartial: '{done} 件成功、{failed} 件失敗しました。',
        currentAudio: '現在の音声',
        noAudio: '音声要素なし',
        statusSearching: '検索中…',
        statusNone: '一致なし',
        pickModalTitle: 'カードの音声を選択',
        currentSelection: '現在の音声',
        clearSelection: '選択を解除',
        chooseExisting: '既存の音声を選択',
        searchPlaceholder: 'メディアライブラリを検索…',
        noResults: '一致する音声が見つかりません。',
        errorLoad: 'カードを読み込めませんでした。',
        noContext:
          'WordPress コンテキストが見つかりません。wp-admin でこのページを開いてください。',
      },
    },
    deckManager: {
      titleFallback: 'デッキ',
      deckCardsTitle: 'デッキカード: {deckTitle}',
      actionsAria: 'デッキ操作',
      titlePlaceholder: 'デッキ名',
      status: 'ステータス',
      leaveUnchanged: '(変更しない)',
      statusDraft: '下書き',
      statusPublish: '公開',
      statusPrivate: '非公開',
      statusPending: '承認待ち',
      allowAnonymous: '匿名アクセスを許可',
      allowAnonymousTooltip:
        '有効にすると、ログインしていない訪問者もこのデッキを閲覧できます。デフォルトはオフです。',
      cancel: 'キャンセル',
      loadingDeck: 'デッキを読み込み中…',
      failedToLoadDeck: 'デッキの読み込みに失敗しました。',
      deckCards: 'デッキのカード',
      availableCards: '利用可能なカード',
      total: '合計 {count}',
      showingCards: '{total}件中{showing}件を表示',
      searchDeckPlaceholder: 'デッキ内を検索…',
      searchDeckAria: 'デッキ内を検索',
      searchAvailablePlaceholder: '利用可能カードを検索…',
      searchAvailableAria: '利用可能カードを検索',
      deckPaginationAria: 'デッキのページ切替',
      availablePaginationAria: '利用可能カードのページ切替',
      deckPerPageAria: 'デッキの表示件数',
      availablePerPageAria: '利用可能カードの表示件数',
      sortAria: '並び替え',
      sortOptions: {
        titleAsc: 'タイトル (A–Z)',
        titleDesc: 'タイトル (Z–A)',
        deckOrder: 'デッキ順',
        newest: '新しい順',
        oldest: '古い順',
      },
      perPage: '{count}件/ページ',
      flipCardAria: 'カードを反転',
      editCardAria: 'カードを編集',
      selectCardAria: 'カードを選択',
      transferAria: '選択カードの移動',
      addAllToDeck: '利用可能なカードをすべてデッキへ追加',
      removeAllFromDeck: 'デッキのカードをすべて削除',
      addSelectedToDeck: '選択をデッキへ追加',
      removeSelectedFromDeck: '選択をデッキから削除',
      confirmRemoveCards: 'デッキから{count}件のカードを削除しますか？{filtered}',
      confirmAddCards: 'デッキに{count}件のカードを追加しますか？{filtered}',
      filteredSuffix: '（フィルター）',
      filteredSearchSuffix: '（検索フィルター）',
      saveFailed: '保存に失敗しました',
      emptyDeck: 'まだカードがありません。右側から選んでデッキに追加してください。',
      noCardsFound: 'カードが見つかりません。',
      noCardsOnPage: 'このページにはカードがありません。',
      allCardsAlreadyInDeck: 'すべてのカードはすでにデッキに入っています。',
    },
    flow: {
      cancel: 'キャンセル',
      save: '保存',
      zoomIn: 'ズームイン',
      zoomOut: 'ズームアウト',
      resetView: 'ビューをリセット',
      scale: 'スケール:',
      nodePalette: 'ノードパレット',
      nodeSettings: 'ノード設定',
      templates: 'テンプレート',
      canvasSettings: 'キャンバス設定',
      visualSettings: 'ビジュアル設定',
      shortcuts: 'ショートカット',
      nodeLabel: 'ノードラベル',
      phaseId: 'フェーズID',
      group: 'グループ',
      macroName: 'マクロ名',
      macroToCall: '呼び出すマクロ',
      leftOperand: '左オペランド',
      leftLiteralValue: '左リテラル値',
      operator: '演算子',
      rightOperand: '右オペランド',
      rightLiteralValue: '右リテラル値',
      action: 'アクション',
      cardSide: 'カード面',
      frontFirst: '表面を先に',
      backFirst: '裏面を先に',
      animation: 'アニメーション',
      followDeckSetting: 'デッキ設定に従う',
      slideLeft: '左にスライド',
      slideRight: '右にスライド',
      flip: 'フリップ',
      fade: 'フェード',
      swipeUp: '上にスワイプ',
      swipeDown: '下にスワイプ',
      revealUnder: '下から表示',
      scaleAnim: 'スケール',
      audioElementIndex: 'オーディオ要素のインデックス',
      waitForAudio: '音声が終わるまで待つ',
      readFrom: '取得元',
      literalValue: 'リテラル値',
      storeInRegister: 'レジスタに保存',
      phaseIdOptional: 'フェーズID（空 = このレベルのフェーズ）',
      setTarget: '設定対象',
      input: '入力',
      operation: '演算',
      cancelConnectionDrag: 'アクティブな接続ドラッグをキャンセル',
      // Palette
      expandPalette: 'パレットを展開',
      collapsePalette: 'パレットを折り畳む',
      expandPanel: 'パネルを展開',
      collapsePanel: 'パネルを折り畳む',
      alreadyOnCanvas: 'すでにキャンバスに配置済み',
      // Node type labels
      nodeType: {
        start: 'セッション開始',
        phase: 'フェーズブロック',
        group: 'グループゲート',
        condition: '条件',
        action: 'アクション',
        macroStart: 'マクロ開始',
        macroEnd: 'マクロ終了',
        macroCall: 'マクロ',
        end: 'フロー終了',
      },
      // Node summary strings
      summary: {
        allCards: 'すべてのカード（デッキ）',
        group: 'グループ',
        noGroupSelected: 'グループ未選択',
        phase: 'フェーズ',
        noPhaseId: 'フェーズIDなし',
        noAction: 'アクションなし',
        entryPoint: 'エントリーポイント',
        in: 'in',
        out: 'out',
        unnamed: '（名前なし）',
        call: '呼び出し',
        noMacroSelected: '（マクロ未選択）',
        terminal: 'ターミナル',
      },
      // Inspector / settings
      typeLabel: 'タイプ',
      allCardsDeck: 'すべてのカード（デッキ）',
      inputsOutputsSymmetric: '入力 / 出力（対称ハンドル）',
      outputsInputsSymmetric: '出力 / 入力（対称ハンドル）',
      selectMacro: '（マクロを選択）',
      inputs: '入力',
      outputs: '出力',
      animNone: 'なし（即時）',
      animConfetti: 'コンフェッティ（＋即時変更）',
      countN: '回数（N）',
      delayMs: '遅延（ms）',
      delayAfterClickMs: 'クリック後の遅延（ms）',
      delayDurationMs: '遅延時間（ms）',
      targetRegister: '対象レジスタ',
      nLiteralValue: 'N（リテラル値）',
      operand: 'オペランド',
      deleteNode: 'ノードを削除',
      deleteConnection: '接続を削除',
      selectedConnection: '選択した接続',
      selectNodeOrConnection: 'ノードまたは接続を選択して編集してください。',
      // Templates panel
      templateNamePlaceholder: 'テンプレート名…',
      saveCurrent: '現在を保存',
      noTemplatesSaved: 'テンプレートが保存されていません。フローを作成して保存してください。',
      doubleClickToRename: 'ダブルクリックで名前を変更',
      addToCanvas: 'キャンバスの中央に追加',
      templateAdd: '追加',
      templateLoad: '読み込む',
      // Canvas settings
      currentScale: '現在のスケール',
      infiniteWorld: '全方向に広がる無限ワールド',
      canvasView: 'ビュー',
      canvasExport: 'エクスポート',
      downloadSvgTitle: 'キャンバスをSVGベクター画像としてダウンロード',
      downloadAsSvg: 'SVGとしてダウンロード',
      canvasDebug: 'デバッグ',
      copyFlowJson: 'フローJSONをコピー',
      // Visual settings
      previewDisplay: 'プレビュー表示',
      disableCardShadows: 'プレビューのカード影を無効化',
      shadowsDesc: 'デフォルトでは影が自然に表示されます。強すぎる場合はこれを有効にしてください。',
      // Shortcuts panel
      keyboardShortcuts: 'キーボードショートカット',
      shortcutsDesc: '選択、移動、グラフ編集のクイック操作。',
      shortcutsAriaLabel: 'フローマネージャーのショートカット一覧',
      shortcutsActionCol: 'アクション',
      shortcutsShortcutCol: 'ショートカット',
      shortcutsGroupCanvasTools: 'キャンバスツール',
      shortcutsTogglePanMarquee: 'パン / マーキー切り替え',
      shortcutsPanHold: 'パン（ホールド）',
      shortcutsAlternatePan: '代替パン',
      shortcutsMarqueeSelection: 'マーキー選択（デフォルト）',
      shortcutsDrag: 'ドラッグ',
      shortcutsDragOnCanvas: 'キャンバスをドラッグ',
      shortcutsGroupCreateNodes: 'ノード作成（カーソル位置）',
      shortcutsCreatePhase: 'フェーズ',
      shortcutsCreateGroupGate: 'グループゲート',
      shortcutsCreatePullCard: 'カードを引く',
      shortcutsCreateFlipCard: 'カードを裏返す',
      shortcutsCreateDelay: '遅延',
      shortcutsCreateMathOp: '数学演算',
      shortcutsCreateCondition: '条件',
      shortcutsGroupSelectionMovement: '選択と移動',
      shortcutsToggleNodeInSelection: '選択にノードを追加/除外',
      shortcutsMoveNodes4px: '選択ノードを移動（4px）',
      shortcutsMoveNodes20px: '選択ノードを移動（20px）',
      shortcutsDeleteSelected: '選択を削除',
      shortcutsCopySelected: '選択をコピー',
      shortcutsCutSelected: '選択を切り取り',
      shortcutsPaste: 'ペースト（接続を含む）',
      shortcutsDuplicateSelected: '選択を複製',
      shortcutsClick: 'クリック',
      shortcutsGroupConnections: '接続',
      shortcutsGroupHistoryFile: '履歴とファイル',
      shortcutsUndo: '取り消す',
      shortcutsRedo: 'やり直す',
      shortcutsSaveFlow: 'フローを保存',
      // Right rail — Preview
      preview: 'プレビュー',
      previewPause: '一時停止',
      previewPauseTitle: '一時停止 — 状態を保持してステップ実行',
      previewStop: '停止',
      previewStopTitle: '停止してリセット',
      previewStep: 'ステップ',
      previewContinue: '続行',
      previewContinueTitle: 'ここから自動再生を再開',
      previewPlay: '再生',
      previewPlayTitle: '自動実行',
      previewClickCardFirst: 'まずカードをクリックしてください',
      previewAdvanceOneStep: '1ステップ進める',
      previewReloadTitle: 'リロード — 保存されたフェーズから再開（実際のユーザーと同様）',
      previewClearSessionTitle: 'セッションをクリア — 進捗を削除して最初から再開',
      playDelay: '再生遅延',
      clickCardToInteract: 'カードをクリックして操作',
      stateNode: 'ノード',
      statePhase: 'フェーズ',
      stateGroup: 'グループ',
      stateRegisters: 'レジスタ',
      stateGetters: 'ゲッター',
      stateSetters: 'セッター',
      previewRequiresWordPress: 'プレビューにはWordPress管理パネルが必要です。',
      clearingSession: 'セッションをクリア中…',
      sessionCleared: 'セッションをクリアしました。',
      clearFailed: 'クリアに失敗しました。',
      // History
      history: '履歴',
      historyUndo: '取り消す',
      historyRedo: 'やり直す',
      historyAddCheckpoint: 'チェックポイントを追加',
      // Toolbar icon titles
      toolbarDuplicate: '複製',
      toolbarCopy: 'コピー',
      toolbarCut: '切り取り',
      toolbarPaste: '貼り付け',
      toolbarDelete: '削除',
      toolbarClearSelection: '選択を解除',
      toolbarSaveSelectionAsTemplate: '選択をテンプレートとして保存',
      toolbarPanTool: 'パンツール',
      toolbarMarqueeTool: 'マーキーツール',
      // Palette items
      palette: {
        categorySession: 'セッション',
        categoryMacros: 'マクロ',
        categoryStructure: '構造',
        categoryCards: 'カード',
        categoryFlow: 'フロー',
        categoryRegisters: 'レジスタ',
        categoryPhase: 'フェーズ',
        categoryMisc: 'その他',
        sessionStart: 'セッション開始',
        sessionStartDesc: 'フローのエントリーポイント。',
        sessionEnd: 'セッション終了',
        sessionEndDesc: 'フローの終了ポイント。',
        macroStart: 'マクロ開始',
        macroStartDesc: '名前付きマクロのエントリー。',
        macroEnd: 'マクロ終了',
        macroEndDesc: 'マクロの終了ポイント。',
        macroCall: 'マクロ',
        macroCallDesc: '名前付きマクロを呼び出す。',
        groupGate: 'グループゲート',
        groupGateDesc: 'アクティブなグループを選択。',
        condition: '条件',
        conditionDesc: '真 / それ以外の分岐。',
        pullCard: 'カードを引く',
        askKnown: '質問: 知っていた？',
        askKnownDesc: '✓ / ✗ ボタンを表示し、known または unknown の出力へ進む。',
        offerPractice: '練習を提案',
        offerPracticeDesc: 'このレベルの後に練習ラウンドを提案する。',
        pullCardDesc: '次のカードを引く。',
        clickCheckbox: 'チェックボックスをクリック',
        clickCheckboxDesc: 'N個のチェックボックスをオンにする。',
        unclickCheckbox: 'チェックボックスをオフ',
        unclickCheckboxDesc: 'N個のチェックボックスをオフにする。',
        flipCard: 'カードを裏返す',
        flipCardDesc: 'カードの表裏を反転。',
        waitClick: 'クリック待機',
        waitClickDesc: 'ユーザーのクリックを待つ。',
        delay: '遅延',
        delayDesc: '続行前に一時停止。',
        earlyExit: '早期終了',
        earlyExitDesc: 'フローを早期に完了。',
        playAudio: 'オーディオ再生',
        playAudioDesc: 'カードのオーディオを再生。',
        action: 'アクション',
        actionDesc: '汎用フローアクション。',
        getValue: '値を取得',
        getValueDesc: 'ソース → レジスタ。',
        setValue: '値を設定',
        setValueDesc: 'レジスタ → ターゲット。',
        math: '数学演算',
        mathDesc: 'レジスタの算術演算。',
        phase: 'フェーズ',
        phaseDesc: 'チェックポイントマーカー。',
        phaseSave: 'フェーズ保存',
        phaseSaveDesc: '進捗チェックポイントを保存。',
        categoryMedia: 'メディア',
        showMedia: 'メディアを表示',
        showMediaDesc: 'コース素材を表示（画像・動画・音声・PDF）。',
      },
      displayMode: '表示モード',
      standalone: '単独表示',
      standaloneDesc: 'フラッシュカードを非表示にしてメディアのみ表示。',
      modal: 'モーダル',
      modalDesc: 'フラッシュカードの上にフローティング表示。',
      split: '分割表示',
      splitDesc: '2つのメディアを左右に並べて表示。',
      panel: 'サイドパネル',
      displayModePanelHint: 'サイドパネルでメディアを開きます。',
      splitRightMedia: '右側（メディア名）',
      splitRightMediaPlaceholder: 'メディア要素名…',
      splitSwap: '左右を入れ替える',
    },
  },
  de: {
    common: {
      back: 'Zurück',
      prev: 'Zurück',
      next: 'Weiter',
      close: 'Schließen',
      cancel: 'Abbrechen',
      clear: 'Leeren',
      continue: 'Fortfahren',
      done: 'Fertig',
      pageIndicator: 'Seite {page} / {total}',
      save: 'Speichern',
      leaveUnchanged: '(unverändert lassen)',
      loading: 'Lädt…',
      total: 'Gesamt',
      successful: 'Erfolgreich',
      failed: 'Fehlgeschlagen',
      upload: 'Hochladen',
      retry: 'Erneut versuchen',
      redo: 'Wiederholen',
      reset: 'Zurücksetzen',
      skip: 'Überspringen',
      undo: 'Rückgängig',
      import: 'Import',
      deck: 'Deck',
      pause: 'Pause',
      play: 'Abspielen',
      rewind10: '10 Sekunden zurück',
      forward10: '10 Sekunden vor',
    },
    deckTemplateEditor: {
      title: 'Deck-Einstellungen: {deckTitle}',
      noDeckLoaded: 'Kein Deck geladen.',
      unset: '(nicht gesetzt)',
      sections: {
        size: 'Größe',
        colors: 'Farben',
        border: 'Rand',
        widgets: 'Widgets',
        shadow: 'Schatten',
        audio: 'Audio',
        motion: 'Übergänge + Flip',
      },
      fields: {
        widthPx: 'Breite (px)',
        heightPx: 'Höhe (px)',
        darkMode: 'Dark Mode',
        background: 'Hintergrund',
        themeBackgroundOverride: 'Theme-Hintergrund (Override)',
        borderColor: 'Randfarbe',
        themeBorderColorOverride: 'Theme-Randfarbe (Override)',
        borderWidthPx: 'Randbreite (px)',
        borderRadiusPx: 'Eckenradius (px)',
        themeBorderRadiusOverride: 'Theme-Radius (Override)',
        shadowCss: 'Schatten (CSS box-shadow)',
        shadowColor: 'Schattenfarbe',
        shadowX: 'X',
        shadowY: 'Y',
        shadowBlur: 'Weichzeichnung',
        shadowSpread: 'Ausdehnung',
        autoplay: 'Autoplay',
        clickToPlay: 'Klicken zum Abspielen',
        audioNotVisible: 'Nicht sichtbar (nur Autoplay)',
        audioForceVisible: 'Audio-Buttons immer anzeigen',
        flowchartHideAudio: 'Audio-Buttons im Flowchart-Modus ausblenden',
        transitionDurationMs: 'Übergangsdauer (ms)',
        themeTransitionDurationOverride: 'Theme-Übergangsdauer (Override)',
        cardChangeAnimation: 'Kartenwechsel-Animation',
        successCardChangeAnimation: 'Erfolgsanimation',
        failedCardChangeAnimation: 'Fehlschlag-Animation',
        flipAnimation: 'Flip-Animation',
        themeFlipAnimationOverride: 'Theme-Flip-Animation (Override)',
        hoverFlip: 'Flip bei Hover',
        showBackFirst: 'Rückseite zuerst anzeigen',

        checkboxTickStyle: 'Checkbox-Markierung',
      },
      tickStyles: {
        fill: 'Gefülltes Quadrat (aktuell)',
        x: 'X',
        haken: 'Haken',
      },
      cardChangeAnimations: {
        none: 'Keine (sofort)',
        fade: 'Überblenden',
        flip: 'Flip (nächste Karte auf der Rückseite)',
        'slide-left': 'Nach links wischen',
        'slide-right': 'Nach rechts wischen',
        'swipe-down': 'Nach unten wischen',
        'reveal-under': 'Unterseite zeigen (unten-links)',
      },
      preview: {
        aria: 'Vorschau',
        title: 'Vorschau',
        emptyCard: 'Leere Karte',
      },
    },
    elements: {
      image: {
        loadError: 'Bild konnte nicht geladen werden.',
      },
    },
    settings: {
      title: 'Einstellungen',
      subtitle: 'Wird lokal in diesem Browser gespeichert.',
      appearanceTitle: 'Darstellung',
      behaviorTitle: 'Verhalten',
      mediaTitle: 'Medien',
      forceDarkModeLabel: 'Dark Mode erzwingen',
      forceDarkModeHelp: 'Flashcards auf diesem Gerät immer im Dark Mode anzeigen.',
      autoFlipLabel: 'Automatisch zurückflippen',
      autoFlipHelp: 'Nach dem Flip automatisch zur Startseite zurückkehren.',
      autoplayAudioLabel: 'Audio automatisch abspielen',
      autoplayAudioHelp:
        'Beim Anzeigen einer Karte Audio automatisch abspielen (Browser kann blockieren).',
      dangerTitle: 'Gefahrenzone',
      dangerHelp: 'Das löscht nur lokale Browserdaten (Einstellungen, Sprache, Editor-Prefs).',
      deleteAllData: 'Alle lokalen Daten löschen',
      deleteConfirm: 'Alle Perlingo-Daten in diesem Browser löschen?',
      deleteConfirm2: 'Das kann nicht rückgängig gemacht werden. Fortfahren?',
    },
    deckPlayer: {
      shell: {
        unsupportedModel: 'Dieses Lernmodell wird noch nicht unterstützt.',
        unauthorized: 'Sie sind nicht berechtigt, diesen Inhalt anzuzeigen.',
        loginHint: 'Nicht eingeloggt — Fortschritt wird nicht gespeichert.',
        flowStart: 'Starten',
        flowPhaseCompleted: 'Gut gemacht!',
        flowContinue: 'Weiter',
        answerKnown: 'Gewusst',
        answerUnknown: 'Nicht gewusst',
        flowReset: 'Fortschritt zurücksetzen',
        flowResetConfirm: 'Wirklich sicher?',
        flowPropagationBlocked: 'Du kannst morgen weitermachen!',
      },
      host: {
        loadingDeck: 'Laden…',
        showMissingCards: 'Fehlende Karten anzeigen',
        showDetails: 'Details anzeigen',
        hideDetails: 'Details ausblenden',
        copyDebugInfo: 'Debug-Infos kopieren',
        copy: 'Kopieren',
        copied: 'Kopiert',
        copyFailed: 'Kopieren fehlgeschlagen: {message}',
        missingCardsHeader: 'Fehlende Karten ({count})',
        untitled: '(ohne Titel)',
        titlesLoadedForFirst: '(Titel für die ersten {count} Karten geladen)',
        skipButton: 'Skipknopf — zum nächsten Knoten springen',
      },
    },
    editor: {
      faces: {
        front: 'Vorderseite',
        back: 'Rückseite',
      },
      elementTypes: {
        text: 'Text',
        image: 'Bild',
        'checkbox-group': 'Checkliste',
        audio: 'Audio',
        video: 'Video',
        shape: 'Form',
      },
      elementDescriptions: {
        text: 'Überschriften, Fließtext oder Hinweise hinzufügen.',
        image: 'Grafiken, Screenshots oder Diagramme einfügen.',
        'checkbox-group': 'Interaktive Checkboxen hinzufügen.',
        audio: 'Audio-Clips abspielen.',
        video: 'Abspielbare Videos einbetten.',
        shape: 'Rechtecke/Ellipsen für Hintergründe und Hervorhebungen.',
      },
      colorPicker: {
        colorPickerAria: 'Farbwähler',
        alphaAria: 'Alpha',
      },
      elementEditors: {
        text: {
          labels: {
            text: 'Text',
            fontFamily: 'Schriftfamilie',
            fontSizePx: 'Schriftgröße (px)',
            lineHeight: 'Zeilenhöhe',
            fontWeight: 'Schriftstärke',
            textColor: 'Textfarbe',
          },
        },
        image: {
          labels: {
            uploadImage: 'Bild hochladen',
            imageUrl: 'Bild-URL',
            altText: 'Alt-Text',
            sizePercent: 'Größe (%)',
            aspectRatio: 'Seitenverhältnis',
            rotationDeg: 'Rotation (°)',
            opacity: 'Deckkraft',
            skewXDeg: 'Schräg X (°)',
            skewYDeg: 'Schräg Y (°)',
          },
          buttons: {
            wpChoose: 'Aus WP-Medien wählen…',
            wpReplace: 'Über WP-Medien ersetzen…',
            remove: 'Entfernen',
          },
          aria: {
            selectedImagePreview: 'Vorschau des ausgewählten Bildes',
            selectedImageAltFallback: 'Ausgewähltes Bild',
          },
          toggles: {
            forceDarkModeStyling: 'Dark-Mode-Stil erzwingen',
            mirrored: 'Gespiegelt',
          },
          wpMedia: {
            titleSelect: 'Bild auswählen',
            titleReplace: 'Bild ersetzen',
            buttonUseThis: 'Dieses Bild verwenden',
          },
        },
        checkboxGroup: {
          labels: {
            count: 'Anzahl',
            direction: 'Richtung',
            boxSize: 'Boxgröße',
            gap: 'Abstand',
            backgroundColor: 'Hintergrundfarbe',
            tickStyle: 'Checkbox-Markierung',
            values: 'Werte',
          },
          options: {
            vertical: 'Vertikal',
            horizontal: 'Horizontal',
          },
        },
        audio: {
          labels: {
            uploadAudio: 'Audio hochladen',
            audioUrl: 'Audio-URL',
            sizePercent: 'Größe (%)',
            iconSizePx: 'Icongröße (px)',
            preload: 'Preload',
            skewXDeg: 'Schräg X (°)',
            skewYDeg: 'Schräg Y (°)',
          },
          options: {
            preload: {
              none: 'Keine',
              metadata: 'Metadaten',
              auto: 'Auto',
            },
          },
          toggles: {
            autoplay: 'Automatisch abspielen',
            notVisible: 'Nicht sichtbar (nur Autoplay)',
            loop: 'Schleife',
            muted: 'Stumm',
          },
        },
        video: {
          labels: {
            uploadVideo: 'Video hochladen',
            videoUrl: 'Video-URL',
            posterUrl: 'Poster-URL',
            sizePercent: 'Größe (%)',
            iconSizePx: 'Icongröße (px)',
            aspectRatio: 'Seitenverhältnis',
            skewXDeg: 'Schräg X (°)',
            skewYDeg: 'Schräg Y (°)',
          },
          toggles: {
            autoplay: 'Automatisch abspielen',
            loop: 'Schleife',
            muted: 'Stumm',
          },
        },
        shape: {
          labels: {
            kind: 'Typ',
            cornerRadius: 'Eckenradius',
            widthPercent: 'Breite (%)',
            heightPercent: 'Höhe (%)',
            fill: 'Füllung',
            stroke: 'Kontur',
            strokeWidth: 'Linienstärke',
            skewXDeg: 'Schräg X (°)',
            skewYDeg: 'Schräg Y (°)',
          },
          options: {
            kind: {
              rectangle: 'Rechteck',
              ellipse: 'Ellipse',
            },
          },
        },
      },
      toolbox: {
        toolboxAria: 'Werkzeugleiste',
        inspectorAria: 'Inspektor',
        clipboardAria: 'Zwischenablage-Aktionen',
        editorActionsAria: 'Editor-Aktionen',
        cardProps: 'Karten-Eigenschaften',
        elementProps: 'Element-Eigenschaften',
        canvasSettings: 'Canvas-Einstellungen',
        previewProps: 'Vorschau-Eigenschaften',
        shortcuts: 'Tastenkürzel',
        audioOrder: 'Audio-Reihenfolge',
        duplicate: 'Duplizieren',
        copy: 'Kopieren',
        cut: 'Ausschneiden',
        paste: 'Einfügen',
        delete: 'Löschen',
        clearSelection: 'Auswahl aufheben',

        status: {
          unsaved: 'Ungespeicherte Änderungen',
          savedAt: 'Gespeichert {time}',
          autosavedAt: 'Auto-gespeichert {time}',
        },
      },
      notifications: {
        uploadMissingContext: 'Upload nicht verfügbar: WordPress REST-Kontext (Nonce/Root) fehlt.',
        uploadFailed: 'Upload fehlgeschlagen: {message}',
        blobNotAllowed:
          'blob:-URLs sind nicht erlaubt. Bitte zu WordPress hochladen oder eine echte URL verwenden.',
      },
      mediaSearch: {
        selectExisting: 'Vorhandene Medien auswählen',
        placeholder: 'Nach Titel oder ID suchen…',
        searching: 'Suche…',
        typeToSearch: 'Zum Suchen tippen.',
        noResults: 'Keine Ergebnisse.',
        untitled: 'Ohne Titel',
        used: 'Verwendet',
        playPreview: 'Abspielen',
        stopPreview: 'Stopp',
        previewFailed: 'Vorschau fehlgeschlagen: {message}',
        searchFailed: 'Suche fehlgeschlagen: {message}',
      },
      historyPanel: {
        title: 'Verlauf',
        actionsAria: 'Verlauf-Aktionen',
        undo: 'Rückgängig',
        redo: 'Wiederholen',
        addCheckpoint: 'Checkpoint hinzufügen',
      },
      layersPanel: {
        title: 'Ebenen',
        actionsAria: 'Ebenen-Aktionen',
        lockLayer: 'Ebene sperren',
        unlockLayer: 'Sperre aufheben',
        moveUp: 'Ebene nach oben',
        moveDown: 'Ebene nach unten',
        deleteLayer: 'Ebene löschen',
        empty: 'Noch keine Elemente auf dieser Seite.',
        textFallback: 'Text',
      },
      canvasSettings: {
        title: 'Canvas-Einstellungen',
        grid: 'Raster',
        gridSize: 'Rastergröße (px)',
        gridSizeHelp: '{size}px · wird auch fürs Snapping genutzt',
        snapStrength: 'Snap-Stärke',
        snapStrengthHelp: '{pct}% · 0% aus, 100% hart',
        measurementUnits: 'Maßeinheiten',
        developerSettings: 'Entwickler-Einstellungen',
        units: {
          px: 'Pixel',
          rem: 'rem',
          percent: 'Prozent',
        },
        showWarningOverlays: 'Warnhinweise anzeigen',
      },
      elementProps: {
        title: 'Element-Eigenschaften',
        noneSelected: 'Wähle ein Element aus, um Eigenschaften zu bearbeiten.',
        selectedSummary: '{type}-Element · {face}',
        multipleSelection: 'Mehrfachauswahl erkannt. Sammelbearbeitung ist noch nicht verfügbar.',
        sections: {
          general: 'Allgemein',
          transform: 'Transform',
        },
        layerTitle: 'Ebenentitel',
        locked: 'Gesperrt',
        positionX: 'Position X (px)',
        positionY: 'Position Y (px)',
        storedRatios: 'Als Verhältnis gespeichert: X {x}, Y {y}',
        noEditableProps: 'Für diesen Elementtyp sind noch keine Eigenschaften verfügbar.',
      },
      audioOrder: {
        title: 'Audio-Reihenfolge',
        noAudio: 'Keine Audio-Elemente auf dieser Karte.',
        front: 'Vorderseite',
        back: 'Rückseite',
        indexLabel: 'Index {index}',
        dragHint: 'Ziehen zum Neuanordnen',
      },
      preview: {
        title: 'Vorschau',
      },
      canvas: {
        faceAria: 'Kartenseite',
        swapFaces: 'Vorder-/Rückseite tauschen',
        totalElements: '{count} Element{suffix} insgesamt',
        emptyTitle: 'Noch keine Elemente auf dieser Seite.',
        emptyHint: 'Nutze die Palette, um Text, Bilder oder Checklisten hinzuzufügen.',
      },
      previewProps: {
        title: 'Vorschau-Eigenschaften',
        subtitle: 'Steuerung für die Vorschau.',
        previewControls: 'Vorschau-Steuerung',
        options: 'Optionen',
        showFront: 'Vorderseite anzeigen',
        showBack: 'Rückseite anzeigen',
        toggle: 'Umschalten',
        reset: 'Zurücksetzen',
        cycleAnimation: 'Animation durchschalten',
        darkMode: 'Dark Mode',
        hoverFlip: 'Flip bei Hover',
        flipAnimation: 'Flip-Animation',
        flipAnimations: {
          'rotate-y': 'Y drehen',
          'rotate-x': 'X drehen',
          slide: 'Schieben',
          fade: 'Überblenden',
          none: 'Keine',
        },
      },
      shortcuts: {
        title: 'Tastenkürzel',
        subtitle: 'Kurzreferenz zum Bearbeiten.',
        regionAria: 'Tastenkürzel',
        colAction: 'Aktion',
        colShortcut: 'Kürzel',
        groupFaces: 'Seiten',
        groupHistory: 'Verlauf',
        groupClipboard: 'Zwischenablage',
        groupSelection: 'Auswahl',
        groupMove: 'Bewegen',
        groupDelete: 'Löschen',
        toggleFrontBack: 'Vorder-/Rückseite umschalten',
        undo: 'Rückgängig',
        redo: 'Wiederholen',
        copy: 'Kopieren',
        cut: 'Ausschneiden',
        paste: 'Einfügen',
        toggleSelection: 'Auswahl umschalten',
        rangeSelectLayers: 'Bereichsauswahl (Ebenen)',
        marqueeSelect: 'Rahmenauswahl',
        marqueeSelectHint: 'Leeren Bereich ziehen',
        nudgeSelection: 'Auswahl verschieben (klein)',
        nudgeLarger: 'Auswahl verschieben (groß)',
        deleteSelected: 'Auswahl löschen',
      },
      cardProps: {
        title: 'Karten-Eigenschaften',
        general: 'Allgemein',
        cardSettings: 'Karteneinstellungen',
        cardTitle: 'Kartentitel',
        status: 'Status',
        widthPx: 'Breite (px)',
        heightPx: 'Höhe (px)',
        borderRadiusPx: 'Eckenradius (px)',
        backgroundColor: 'Hintergrundfarbe',
        transitionDurationMs: 'Übergangsdauer (ms)',
        checkboxTickStyle: 'Checkbox-Markierung',
        outline: 'Umrandung',
        outlineColor: 'Umrandungsfarbe',
        outlineWidthPx: 'Umrandungsstärke (px)',
        shadow: 'Schatten',
        shadowColor: 'Schattenfarbe',
        shadowX: 'Schatten X',
        shadowY: 'Schatten Y',
        shadowBlur: 'Schatten weichzeichnen',
        shadowSpread: 'Schattenausbreitung',
        flip: 'Flip',
        flipAnimation: 'Flip-Animation',
        hoverFlip: 'Flip bei Hover',
        showBackFirst: 'Rückseite zuerst anzeigen',
        darkMode: 'Dark Mode',
        theme: 'Theme',
        themeHelp: 'Theme-Overrides sind optional.',
        themeBackground: 'Theme-Hintergrund',
        themeBorderColor: 'Theme-Randfarbe',
        themeBorderRadiusPx: 'Theme-Eckenradius (px)',
        themeTransitionDurationMs: 'Theme-Übergangsdauer (ms)',
        themeFlipAnimation: 'Theme-Flip-Animation',
        emptyNoOverride: '(leer = kein Override)',
        inherit: 'Übernehmen',
        wpStatus: {
          draft: 'Entwurf',
          publish: 'Veröffentlicht',
          private: 'Privat',
          pending: 'Ausstehend',
        },
      },
      history: {
        initial: 'Ausgangszustand',
        load: 'Laden',
        save: 'Speichern',
        autosave: 'Auto-Speichern',
        checkpoint: 'Checkpoint',
        set: '{key} setzen',
        addElement: '{type}-Element hinzufügen',
        updateElement: 'Element aktualisieren',
        deleteElement: 'Element löschen',
        setTitle: 'Titel setzen',
        setStatus: 'Status setzen',
        swapFaces: 'Seiten tauschen',
        pasteElements: 'Elemente einfügen',
        deleteSelected: 'Ausgewählte Elemente löschen',
        reorderLayers: 'Ebenen neu anordnen',
        moveElement: 'Element bewegen',
        moveElements: 'Elemente bewegen',
        nudgeElement: 'Element verschieben',
        nudgeElements: 'Elemente verschieben',
        resizeImage: 'Bildgröße ändern',
        rotateImage: 'Bild drehen',
        resizeElement: 'Elementgröße ändern',
        rotateElement: 'Element drehen',
        confirmSave: 'Bist du sicher? Das speichert deine Änderungen.',
        confirmRevert: 'Änderungen verwerfen und zum letzten Speichern zurückkehren?',
        pastedImageAlt: 'Eingefügtes Bild',
        toggleLock: 'Sperre umschalten',
        updateText: 'Text aktualisieren',
        updateFontFamily: 'Schriftfamilie ändern',
        updateFontSize: 'Schriftgröße ändern',
        updateLineHeight: 'Zeilenhöhe ändern',
        updateFontWeight: 'Schriftstärke ändern',
        updateTextColor: 'Textfarbe ändern',
        setImage: 'Bild setzen',
        removeImage: 'Bild entfernen',
        updateImageSrc: 'Bild-URL ändern',
        updateImageAlt: 'Alt-Text ändern',
        updateAspectRatio: 'Seitenverhältnis ändern',
        toggleDarkModeOverride: 'Dark-Mode-Stil umschalten',
        toggleMirrored: 'Spiegelung umschalten',
        updateRotation: 'Rotation ändern',
        updateOpacity: 'Deckkraft ändern',
        updateSkewX: 'Schräg X ändern',
        updateSkewY: 'Schräg Y ändern',
        updateCheckboxCount: 'Checkbox-Anzahl ändern',
        toggleCheckbox: 'Checkbox umschalten',
        updateDirection: 'Richtung ändern',
        updateBoxSize: 'Boxgröße ändern',
        updateGap: 'Abstand ändern',
        updateCheckboxTickStyle: 'Checkbox-Markierung ändern',
        updateBackgroundColor: 'Hintergrundfarbe ändern',
        setAudio: 'Audio setzen',
        updateAudioSrc: 'Audio-URL ändern',
        updateAudioSize: 'Audio-Größe ändern',
        updateAudioIconSize: 'Audio-Icongröße ändern',
        updatePreload: 'Preload ändern',
        toggleAutoplay: 'Autoplay umschalten',
        toggleNotVisible: 'Unsichtbar umschalten',
        toggleLoop: 'Loop umschalten',
        toggleMuted: 'Stumm umschalten',
        setVideo: 'Video setzen',
        updateVideoSrc: 'Video-URL ändern',
        updateVideoSize: 'Video-Größe ändern',
        updateVideoIconSize: 'Video-Icongröße ändern',
        updatePoster: 'Poster ändern',
        updateShapeKind: 'Formtyp ändern',
        updateRadius: 'Radius ändern',
        updateWidth: 'Breite ändern',
        updateHeight: 'Höhe ändern',
        updateFill: 'Füllung ändern',
        updateStroke: 'Kontur ändern',
        updateStrokeWidth: 'Linienstärke ändern',
        renameLayer: 'Ebene umbenennen',
        updatePosition: 'Position aktualisieren',
        updateTitle: 'Titel aktualisieren',
        toggleLockState: 'Sperrstatus umschalten',
        reorderAudio: 'Audio-Elemente neu anordnen',
      },
    },
    import: {
      wizard: {
        stepsAria: 'Import-Schritte',
      },
      chooseType: {
        title: 'Import-Typ wählen',
        pdf: { title: 'PDF-Import' },
        json: { title: 'JSON-Import' },
        continuePdf: 'Mit PDF fortfahren',
        continueJson: 'Mit JSON fortfahren',
        scopeLabel: 'Was importieren',
        scope: {
          cards: 'Karten',
          decks: 'Stapel',
          courses: 'Kurse',
          all: 'Alles',
        },
      },
      sides: {
        label: 'Karteikartenseiten',
        one: 'Einseitige Karteikarten importieren',
        two: 'Zweiseitige Karteikarten importieren',
        oneShort: '1 Seite',
        twoShort: '2 Seiten',
        front: 'Vorderseite',
        back: 'Rückseite',
        frontPdf: 'PDF der Vorderseiten',
        backPdf: 'PDF der Rückseiten',
        pages: '{count} Seiten',
        frontStart: 'Erste Vorderseite',
        backStart: 'Erste Rückseite',
        pagePairs: 'Zu importierende Seitenpaare',
        pairingHint:
          'Karten werden nach Seite und Position zugeordnet, von links nach rechts und oben nach unten.',
        rangeSummary:
          'Vorderseiten {frontStart}–{frontEnd} ↔ Rückseiten {backStart}–{backEnd}. Seiten außerhalb dieser Bereiche werden nicht importiert.',
        invalidRange: 'Bitte gültige Seitenbereiche gleicher Länge in beiden PDFs auswählen.',
        preview: 'Vorschauseite',
        sharedGrid:
          'Beide Seiten verwenden dasselbe Raster. Das Ausschließen einer Karte gilt für beide Seiten.',
        loadError:
          'Die PDF konnte nicht geladen werden. Bitte eine gültige PDF auswählen und erneut versuchen.',
        cutError:
          'Die beiden Seiten konnten nicht vorbereitet werden. Bitte zurückgehen und erneut versuchen.',
      },
      uploadPdf: {
        title: 'PDF hochladen',
        dropTitle: 'PDF hier ablegen',
        dropHint: 'oder klicken, um eine Datei auszuwählen',
        meta: 'Nur PDF · max. Größe browserabhängig',
      },
      recognition: {
        title: 'Kartenerkennung',
        ai: 'AI',
        algorithm: 'Algorithmus',
        aiHint: 'Codex liest jede Seite (bis zu 10 Seiten pro Anfrage).',
        algorithmHint: 'Liest die Textebene des PDFs, wie bisher.',
        checking: 'Codex wird geprüft…',
        running: 'Codex liest Seitengruppe {done}/{total}…',
        unavailable: 'AI-Erkennung nicht verfügbar: {reason} Es wird der Algorithmus verwendet.',
        failed:
          'AI-Erkennung fehlgeschlagen: {reason} Stattdessen wurde der Algorithmus verwendet.',
        noContext: 'WordPress-Admin-Kontext fehlt.',
        notConfigured: 'sie ist nicht eingerichtet (Einstellungen → Entwickler).',
        timeout: 'Codex hat nicht rechtzeitig geantwortet.',
        incomplete: 'Codex hat nicht alle Karten geliefert.',
      },
      configureGrid: {
        title: 'Raster-Konfiguration',
        columns: 'Spalten',
        rows: 'Zeilen',
        gridWidthMm: 'Rasterbreite (mm)',
        gridHeightMm: 'Rasterhöhe (mm)',
        offsetXmm: 'Offset X (mm)',
        offsetYmm: 'Offset Y (mm)',
        paddingLeftMm: 'Innenabstand links (mm, je Karte)',
        paddingRightMm: 'Innenabstand rechts (mm, je Karte)',
        paddingTopMm: 'Innenabstand oben (mm, je Karte)',
        paddingBottomMm: 'Innenabstand unten (mm, je Karte)',
      },
      reviewCuts: {
        cutPreviewAlt: 'Ausschnitt-Vorschau',
        excluded: 'Ausgeschlossen',
        processingCuts: 'Karten werden ausgeschnitten…',
        deckConfigTitle: 'Deck-Konfiguration',
        deckTitle: 'Deck-Titel',
        deckTitlePlaceholder: 'Mein Deck',
        cardIdentifier: 'Kartenkennung (Präfix)',
        cardIdentifierPlaceholder: 'z. B. Vorkurs',
        cardIdentifierHint: 'Wird als Präfix genutzt: „Kennung – Kartenname“.',
        cardWidthPx: 'Kartenbreite (px)',
        cardHeightPx: 'Kartenhöhe (px)',
        darkMode: 'Dark Mode',
        background: 'Hintergrund',
        borderColor: 'Randfarbe',
        borderRadiusPx: 'Eckenradius (px)',
        transitionMs: 'Übergang (ms)',
        flipAnimation: 'Flip-Animation',
        flipOnHover: 'Flip bei Hover',
        showBackFirst: 'Rückseite zuerst anzeigen',
        themeOverrides: 'Theme-Overrides (optional)',
        themeBackground: 'Theme-Hintergrund',
        themeBorderColor: 'Theme-Randfarbe',
        themeRadiusPx: 'Theme-Radius (px)',
        themeTransitionMs: 'Theme-Übergang (ms)',
        themeFlipAnimation: 'Theme-Flip-Animation',
        emptyNoOverride: '(leer = kein Override)',
        deckTitleRequired: 'Ein Deck-Titel ist vor dem Upload erforderlich.',
      },
      audioMatch: {
        title: 'Audio-Zuordnung',
        empty: 'Keine Karten zum Zuordnen (alle ausgeschlossen).',
        untitled: '(ohne Titel)',
        selected: '(ausgewählt)',
        noMatch: 'Keine Übereinstimmung',
        multipleCandidates: '{count} Treffer',
        summary: {
          matched: '{count} zugeordnet',
          multiple: '{count} prüfen',
          missing: '{count} fehlt',
        },
        buttons: {
          autoMatch: 'Auto-Zuordnung',
          forceRematch: 'Erneut zuordnen (erzwingen)',
          clear: 'Auswahl löschen',
        },
        modal: {
          title: 'Audio für Karte zuordnen',
          current: 'Aktuelles Audio',
          uploadNew: 'Neues Audio hochladen',
          chooseExisting: 'Vorhandenes Audio auswählen',
          searchPlaceholder: 'Nach Titel oder ID suchen…',
          typeToSearch: 'Zum Suchen tippen…',
          noResults: 'Keine Ergebnisse.',
        },
        errors: {
          missingWp: 'WordPress REST-Kontext fehlt (root/nonce).',
          searchFailed: 'Suche fehlgeschlagen.',
          uploadFailed: 'Upload fehlgeschlagen.',
        },
      },
      upload: {
        title: 'Upload',
        aboutToUpload: 'Du bist dabei, {count} Karten hochzuladen.',
        uploading: 'Lädt hoch… {pct}%',
        confirmHint:
          'Dadurch werden Medien und Karten in WordPress erstellt. Du kannst Fehler erneut versuchen und den Import rückgängig machen.',
        completeFinalized: 'Upload abgeschlossen. Deck wurde finalisiert.',
        finishedWithFailures: 'Upload mit Fehlern abgeschlossen.',
        finished: 'Upload abgeschlossen.',
        showFailedItems: 'Fehlgeschlagene Elemente anzeigen',
        unknownError: 'Unbekannter Fehler',
        undoComplete: 'Rückgängig abgeschlossen. Erstellte Ressourcen wurden gelöscht.',
        uploadAgain: 'Du kannst zurückgehen und erneut hochladen.',
        retryFailed: 'Fehlgeschlagenes erneut versuchen',
        undoUpload: 'Upload rückgängig',
        backToReview: 'Zurück zur Prüfung',
      },
    },
    tools: {
      tabs: {
        general: 'Allgemein',
        students: 'Werkzeuge',
      },
      users: {
        intro:
          'Verwalte Nutzerkonten: Kurszugriff, Münzen, Fortschritt und Passwörter — einzeln oder gebündelt. Die Gruppe „Allgemein“ enthält alle nicht generierten Konten (Administratoren, normale Registrierungen).',
        generalGroup: 'Allgemein',
        adminBadge: 'Admin',
        bulkTitle: '{count} ausgewählt',
        bulkGrant: 'Kurs freischalten',
        bulkRevoke: 'Kurs entziehen',
        bulkAddCoins: 'Münzen hinzufügen',
        bulkSetCoins: 'Münzen setzen',
        bulkAddPearls: 'Perlen hinzufügen',
        bulkSetPearls: 'Perlen setzen',
        bulkResetProgress: 'Fortschritt zurücksetzen',
        bulkResetPasswords: 'Passwörter zurücksetzen',
        bulkDelete: 'Löschen',
        courseSelect: 'Kurs wählen…',
        allCourses: 'Alle Kurse',
        coins: 'Münzen',
        pearls: 'Perlen',
        access: 'Zugriff',
        coursesColumn: 'Kurse',
        progressTitle: 'Kurse & Fortschritt',
        completeCourse: 'Abschließen',
        resetCourse: 'Zurücksetzen',
        levelsDone: '{done}/{total} Level',
        free: 'kostenlos',
        details: 'Details',
        updatedNotice: '{count} Nutzer aktualisiert.',
        resetProgressConfirm: 'Fortschritt von {count} Nutzer(n) zurücksetzen?',
        resetPwConfirm: 'Neue Passwörter für {count} Nutzer erzeugen?',
        errorAction: 'Aktion fehlgeschlagen.',
        needCourse: 'Bitte zuerst einen Kurs wählen.',
      },
      students: {
        intro:
          'Erzeuge anonyme Schülerkonten. Benutzernamen sind zufällig und enthalten keine persönlichen Daten; die Gruppe dient nur zur Organisation (z. B. nach Schule oder Klasse).',
        accessTitle: 'Kurszugriff',
        accessHint: 'Neu erzeugte Konten erhalten diese Kurse sofort freigeschaltet.',
        manageHint: 'Bestehende Konten verwaltest du im Tab „Allgemein“.',
        grantFailed: 'Konten wurden erstellt, aber die Kurs-Freischaltung ist fehlgeschlagen.',
        groupTitle: 'Gruppe',
        newGroup: 'Neue Gruppe',
        newGroupPlaceholder: 'z. B. schule-linz',
        accountsCount: '{count} Konten',
        generateTitle: 'Konten erzeugen',
        countLabel: 'Wie viele Konten?',
        generate: 'Erzeugen',
        generating: 'Erzeuge…',
        listTitle: 'Konten für „{group}“',
        loading: 'Lade…',
        empty: 'Noch keine Konten in dieser Gruppe.',
        username: 'Benutzername',
        password: 'Passwort',
        registered: 'Erstellt',
        actions: 'Aktionen',
        resetPassword: 'Passwort zurücksetzen',
        resetConfirm: 'Neues Passwort für {username} erzeugen?',
        delete: 'Löschen',
        deleteSelected: 'Ausgewählte löschen ({count})',
        deleteConfirm:
          '{count} Schülerkonto/-konten löschen? Das kann nicht rückgängig gemacht werden.',
        selectAll: 'Alle wählen',
        deselectAll: 'Alle abwählen',
        newCredentialsTitle: 'Neue Zugangsdaten',
        newCredentialsHint:
          'Passwörter werden nur einmal angezeigt. Jetzt drucken — sie können später nicht erneut angezeigt werden.',
        print: 'Zugangsdaten drucken',
        clearCredentials: 'Liste leeren',
        printSheetTitle: 'Zugangsdaten',
        printSite: 'Webseite',
        generatedNotice: '{count} Konto/Konten erstellt.',
        deletedNotice: '{count} Konto/Konten gelöscht.',
        errorLoad: 'Daten konnten nicht geladen werden.',
        errorGenerate: 'Konten konnten nicht erzeugt werden.',
        errorDelete: 'Löschen fehlgeschlagen.',
        errorReset: 'Passwort-Zurücksetzen fehlgeschlagen.',
        noContext: 'WordPress-Kontext nicht gefunden. Seite in wp-admin öffnen.',
      },
      audio: {
        overlayTitle: 'Audio zurücksetzen',
        intro:
          'Kartenaudio gebündelt korrigieren: Karten in der Kartenliste auswählen, dort bei Massenaktionen „Audio zurücksetzen“ wählen — oder unten gezielt Karten-IDs laden.',
        idsPlaceholder: 'Karten-IDs, z. B. 12, 45, 78',
        load: 'Laden',
        loading: 'Lade…',
        empty:
          'Noch keine Karten geladen. Karten in der Kartenliste auswählen und bei Massenaktionen „Audio zurücksetzen“ wählen, oder oben IDs laden.',
        groupFilter: 'Gruppe',
        allGroups: 'Alle Gruppen',
        selectAll: 'Alle auswählen',
        matching: 'Gleiche ab…',
        rerun: 'Erneut abgleichen',
        summaryMatched: '{count} gefunden',
        summaryMultiple: '{count} zu prüfen',
        summaryMissing: '{count} fehlend',
        multipleCandidates: '{count} Treffer',
        apply: 'Audio zurücksetzen ({count} ausgewählt)',
        applying: 'Wende an…',
        applyDone: '{count} Karte(n) aktualisiert.',
        applyPartial: '{done} aktualisiert, {failed} fehlgeschlagen.',
        currentAudio: 'Aktuelles Audio',
        noAudio: 'Kein Audio-Element',
        statusSearching: 'Suche…',
        statusNone: 'Kein Treffer',
        pickModalTitle: 'Audio für Karte wählen',
        currentSelection: 'Aktuelles Audio',
        clearSelection: 'Auswahl aufheben',
        chooseExisting: 'Vorhandenes Audio wählen',
        searchPlaceholder: 'Mediathek durchsuchen…',
        noResults: 'Kein passendes Audio gefunden.',
        errorLoad: 'Karten konnten nicht geladen werden.',
        noContext: 'WordPress-Kontext nicht gefunden. Seite in wp-admin öffnen.',
      },
    },
    deckManager: {
      titleFallback: 'Deck',
      deckCardsTitle: 'Deck-Karten: {deckTitle}',
      actionsAria: 'Deck-Aktionen',
      titlePlaceholder: 'Deck-Titel',
      status: 'Status',
      leaveUnchanged: '(unverändert lassen)',
      statusDraft: 'Entwurf',
      statusPublish: 'Veröffentlicht',
      statusPrivate: 'Privat',
      statusPending: 'Ausstehend',
      allowAnonymous: 'Anonymen Zugriff erlauben',
      allowAnonymousTooltip:
        'Wenn aktiviert, können Besucher ohne Login dieses Deck ansehen. Standardmäßig deaktiviert.',
      cancel: 'Abbrechen',
      loadingDeck: 'Deck wird geladen…',
      failedToLoadDeck: 'Deck konnte nicht geladen werden.',
      deckCards: 'Deck-Karten',
      availableCards: 'Verfügbare Karten',
      total: '{count} insgesamt',
      showingCards: '{showing} von {total} Karten angezeigt',
      searchDeckPlaceholder: 'Deck-Karten durchsuchen…',
      searchDeckAria: 'Deck-Karten durchsuchen',
      searchAvailablePlaceholder: 'Verfügbare Karten durchsuchen…',
      searchAvailableAria: 'Verfügbare Karten durchsuchen',
      deckPaginationAria: 'Deck-Paginierung',
      availablePaginationAria: 'Paginierung verfügbar',
      deckPerPageAria: 'Deck pro Seite',
      availablePerPageAria: 'Verfügbar pro Seite',
      sortAria: 'Sortieren',
      sortOptions: {
        titleAsc: 'Titel (A–Z)',
        titleDesc: 'Titel (Z–A)',
        deckOrder: 'Deck-Reihenfolge',
        newest: 'Neueste',
        oldest: 'Älteste',
      },
      perPage: '{count} pro Seite',
      flipCardAria: 'Karte umdrehen',
      editCardAria: 'Karte bearbeiten',
      selectCardAria: 'Karte auswählen',
      transferAria: 'Ausgewählte Karten übertragen',
      addAllToDeck: 'Alle verfügbaren Karten zum Deck hinzufügen',
      removeAllFromDeck: 'Alle Deck-Karten entfernen',
      addSelectedToDeck: 'Auswahl zum Deck hinzufügen',
      removeSelectedFromDeck: 'Auswahl aus dem Deck entfernen',
      confirmRemoveCards: '{count} Karte(n) aus dem Deck entfernen?{filtered}',
      confirmAddCards: '{count} Karte(n) zum Deck hinzufügen?{filtered}',
      filteredSuffix: ' (gefiltert)',
      filteredSearchSuffix: ' (gefilterte Suche)',
      saveFailed: 'Speichern fehlgeschlagen',
      emptyDeck: 'Noch keine Karten. Wähle rechts Karten aus und füge sie dem Deck hinzu.',
      noCardsFound: 'Keine Karten gefunden.',
      noCardsOnPage: 'Keine Karten auf dieser Seite.',
      allCardsAlreadyInDeck: 'Alle Karten sind bereits im Deck.',
    },
    flow: {
      cancel: 'Abbrechen',
      save: 'Speichern',
      zoomIn: 'Zoom +',
      zoomOut: 'Zoom -',
      resetView: 'Ansicht zurücksetzen',
      scale: 'Maßstab:',
      nodePalette: 'Knoten-Palette',
      nodeSettings: 'Knoten-Einstellungen',
      templates: 'Vorlagen',
      canvasSettings: 'Canvas-Einstellungen',
      visualSettings: 'Visuelle Einstellungen',
      shortcuts: 'Tastenkürzel',
      nodeLabel: 'Knoten-Label',
      phaseId: 'Phasen-ID',
      group: 'Gruppe',
      macroName: 'Makro-Name',
      macroToCall: 'Aufzurufendes Makro',
      leftOperand: 'Linker Operand',
      leftLiteralValue: 'Linker Literalwert',
      operator: 'Operator',
      rightOperand: 'Rechter Operand',
      rightLiteralValue: 'Rechter Literalwert',
      action: 'Aktion',
      cardSide: 'Kartenseite',
      frontFirst: 'Vorderseite zuerst',
      backFirst: 'Rückseite zuerst',
      animation: 'Animation',
      followDeckSetting: 'Deck-Einstellung verwenden',
      slideLeft: 'Nach links gleiten',
      slideRight: 'Nach rechts gleiten',
      flip: 'Flip',
      fade: 'Überblenden',
      swipeUp: 'Nach oben wischen',
      swipeDown: 'Nach unten wischen',
      revealUnder: 'Unterseite zeigen',
      scaleAnim: 'Skalierung',
      audioElementIndex: 'Audio-Element-Index',
      waitForAudio: 'Warten bis Audio fertig ist',
      readFrom: 'Lesen von',
      literalValue: 'Literalwert',
      storeInRegister: 'In Register speichern',
      phaseIdOptional: 'Phasen-ID (leer = Phase dieses Levels)',
      setTarget: 'Ziel setzen',
      input: 'Eingabe',
      operation: 'Operation',
      cancelConnectionDrag: 'Aktives Verbindungsziehen abbrechen',
      // Palette
      expandPalette: 'Palette ausklappen',
      collapsePalette: 'Palette einklappen',
      expandPanel: 'Panel ausklappen',
      collapsePanel: 'Panel einklappen',
      alreadyOnCanvas: 'Bereits auf der Leinwand platziert',
      // Node type labels
      nodeType: {
        start: 'Sitzungsstart',
        phase: 'Phasenblock',
        group: 'Gruppentor',
        condition: 'Bedingung',
        action: 'Aktion',
        macroStart: 'Makro-Beginn',
        macroEnd: 'Makro-Ende',
        macroCall: 'Makro',
        end: 'Fluss-Ende',
      },
      // Node summary strings
      summary: {
        allCards: 'Alle Karten (Deck)',
        group: 'Gruppe',
        noGroupSelected: 'Keine Gruppe ausgewählt',
        phase: 'Phase',
        noPhaseId: 'Keine Phasen-ID',
        noAction: 'Keine Aktion',
        entryPoint: 'Einstiegspunkt',
        in: 'ein',
        out: 'aus',
        unnamed: '(unbenannt)',
        call: 'Aufruf',
        noMacroSelected: '(kein Makro gewählt)',
        terminal: 'Terminal',
      },
      // Inspector / settings
      typeLabel: 'Typ',
      allCardsDeck: 'Alle Karten (Deck)',
      inputsOutputsSymmetric: 'Eingaben / Ausgaben (symmetrische Handles)',
      outputsInputsSymmetric: 'Ausgaben / Eingaben (symmetrische Handles)',
      selectMacro: '(Makro wählen)',
      inputs: 'Eingabe(n)',
      outputs: 'Ausgabe(n)',
      animNone: 'Keine (sofort)',
      animConfetti: 'Konfetti (+ sofortiger Wechsel)',
      countN: 'Anzahl (N)',
      delayMs: 'Verzögerung (ms)',
      delayAfterClickMs: 'Verzögerung nach Klick (ms)',
      delayDurationMs: 'Verzögerungsdauer (ms)',
      targetRegister: 'Zielregister',
      nLiteralValue: 'N (Literalwert)',
      operand: 'Operand',
      deleteNode: 'Knoten löschen',
      deleteConnection: 'Verbindung löschen',
      selectedConnection: 'Ausgewählte Verbindung',
      selectNodeOrConnection: 'Knoten oder Verbindung zum Bearbeiten auswählen.',
      // Templates panel
      templateNamePlaceholder: 'Vorlagenname…',
      saveCurrent: 'Aktuellen speichern',
      noTemplatesSaved: 'Noch keine Vorlagen gespeichert. Einen Fluss erstellen und speichern.',
      doubleClickToRename: 'Doppelklick zum Umbenennen',
      addToCanvas: 'Zur Leinwand-Mitte hinzufügen',
      templateAdd: 'Hinzufügen',
      templateLoad: 'Laden',
      // Canvas settings
      currentScale: 'Aktueller Maßstab',
      infiniteWorld: 'Unendliche Welt, in alle Richtungen zentriert',
      canvasView: 'Ansicht',
      canvasExport: 'Export',
      downloadSvgTitle: 'Leinwand als SVG-Vektorbild herunterladen',
      downloadAsSvg: 'Als SVG herunterladen',
      canvasDebug: 'Debug',
      copyFlowJson: 'Fluss-JSON kopieren',
      // Visual settings
      previewDisplay: 'Vorschau-Anzeige',
      disableCardShadows: 'Kartenschatten in der Vorschau deaktivieren',
      shadowsDesc:
        'Standardmäßig überlaufen Schatten natürlich. Aktivieren, wenn sie zu stark sind.',
      // Shortcuts panel
      keyboardShortcuts: 'Tastaturkürzel',
      shortcutsDesc: 'Schnellsteuerung für Auswahl, Bewegung und Graph-Bearbeitung.',
      shortcutsAriaLabel: 'Tastaturkürzel-Referenz des Flussmanagers',
      shortcutsActionCol: 'Aktion',
      shortcutsShortcutCol: 'Tastenkürzel',
      shortcutsGroupCanvasTools: 'Leinwand-Werkzeuge',
      shortcutsTogglePanMarquee: 'Pan / Marquee umschalten',
      shortcutsPanHold: 'Schwenken (halten)',
      shortcutsAlternatePan: 'Alternatives Schwenken',
      shortcutsMarqueeSelection: 'Marquee-Auswahl (Standard)',
      shortcutsDrag: 'Ziehen',
      shortcutsDragOnCanvas: 'Auf Leinwand ziehen',
      shortcutsGroupCreateNodes: 'Knoten erstellen (am Cursor)',
      shortcutsCreatePhase: 'Phase',
      shortcutsCreateGroupGate: 'Gruppentor',
      shortcutsCreatePullCard: 'Karte ziehen',
      shortcutsCreateFlipCard: 'Karte umdrehen',
      shortcutsCreateDelay: 'Verzögerung',
      shortcutsCreateMathOp: 'Rechenoperation',
      shortcutsCreateCondition: 'Bedingung',
      shortcutsGroupSelectionMovement: 'Auswahl & Bewegung',
      shortcutsToggleNodeInSelection: 'Knoten zur Auswahl hinzufügen/entfernen',
      shortcutsMoveNodes4px: 'Ausgewählte Knoten bewegen (4px)',
      shortcutsMoveNodes20px: 'Ausgewählte Knoten bewegen (20px)',
      shortcutsDeleteSelected: 'Auswahl löschen',
      shortcutsCopySelected: 'Auswahl kopieren',
      shortcutsCutSelected: 'Auswahl ausschneiden',
      shortcutsPaste: 'Einfügen (mit internen Verbindungen)',
      shortcutsDuplicateSelected: 'Auswahl duplizieren',
      shortcutsClick: 'Klick',
      shortcutsGroupConnections: 'Verbindungen',
      shortcutsGroupHistoryFile: 'Verlauf & Datei',
      shortcutsUndo: 'Rückgängig',
      shortcutsRedo: 'Wiederholen',
      shortcutsSaveFlow: 'Fluss speichern',
      // Right rail — Preview
      preview: 'Vorschau',
      previewPause: 'Pause',
      previewPauseTitle: 'Pause — Zustand behalten, manuell schrittweise ausführen',
      previewStop: 'Stopp',
      previewStopTitle: 'Stoppen und zurücksetzen',
      previewStep: 'Schritt',
      previewContinue: 'Fortfahren',
      previewContinueTitle: 'Automatische Wiedergabe von hier fortsetzen',
      previewPlay: 'Abspielen',
      previewPlayTitle: 'Automatisch ausführen',
      previewClickCardFirst: 'Zuerst die Karte anklicken',
      previewAdvanceOneStep: 'Einen Schritt vorwärts',
      previewReloadTitle: 'Neu laden — setzt ab gespeicherter Phase fort (wie ein echter Benutzer)',
      previewClearSessionTitle:
        'Sitzung löschen — Fortschritt entfernen und von Anfang neu starten',
      playDelay: 'Wiedergabeverzögerung',
      clickCardToInteract: 'Karte anklicken zur Interaktion',
      stateNode: 'Knoten',
      statePhase: 'Phase',
      stateGroup: 'Gruppe',
      stateRegisters: 'Register',
      stateGetters: 'Getter',
      stateSetters: 'Setter',
      previewRequiresWordPress: 'Vorschau erfordert das WordPress-Adminpanel.',
      clearingSession: 'Sitzung wird gelöscht…',
      sessionCleared: 'Sitzung gelöscht.',
      clearFailed: 'Löschen fehlgeschlagen.',
      // History
      history: 'Verlauf',
      historyUndo: 'Rückgängig',
      historyRedo: 'Wiederholen',
      historyAddCheckpoint: 'Checkpoint hinzufügen',
      // Toolbar icon titles
      toolbarDuplicate: 'Duplizieren',
      toolbarCopy: 'Kopieren',
      toolbarCut: 'Ausschneiden',
      toolbarPaste: 'Einfügen',
      toolbarDelete: 'Löschen',
      toolbarClearSelection: 'Auswahl aufheben',
      toolbarSaveSelectionAsTemplate: 'Auswahl als Vorlage speichern',
      toolbarPanTool: 'Schwenk-Werkzeug',
      toolbarMarqueeTool: 'Marquee-Werkzeug',
      // Palette items
      palette: {
        categorySession: 'Sitzung',
        categoryMacros: 'Makros',
        categoryStructure: 'Struktur',
        categoryCards: 'Karten',
        categoryFlow: 'Fluss',
        categoryRegisters: 'Register',
        categoryPhase: 'Phase',
        categoryMisc: 'Sonstiges',
        sessionStart: 'Sitzungsstart',
        sessionStartDesc: 'Einstiegspunkt des Flusses.',
        sessionEnd: 'Sitzungsende',
        sessionEndDesc: 'Ausstiegspunkt des Flusses.',
        macroStart: 'Makro-Beginn',
        macroStartDesc: 'Benannter Makro-Einstieg.',
        macroEnd: 'Makro-Ende',
        macroEndDesc: 'Makro-Ausstiegspunkt.',
        macroCall: 'Makro',
        macroCallDesc: 'Ein benanntes Makro aufrufen.',
        groupGate: 'Gruppentor',
        groupGateDesc: 'Aktive Gruppe auswählen.',
        condition: 'Bedingung',
        conditionDesc: 'Wahr / sonst-Zweig.',
        pullCard: 'Karte ziehen',
        askKnown: 'Frage: gewusst?',
        askKnownDesc: '✓ / ✗ Buttons zeigen; weiter über Ausgang known oder unknown.',
        offerPractice: 'Übungsrunde anbieten',
        offerPracticeDesc: 'Nach diesem Level die Übungsrunde anbieten.',
        pullCardDesc: 'Nächste Karte ziehen.',
        clickCheckbox: 'Checkbox anklicken',
        clickCheckboxDesc: 'N Checkboxen aktivieren.',
        unclickCheckbox: 'Checkbox abwählen',
        unclickCheckboxDesc: 'N Checkboxen deaktivieren.',
        flipCard: 'Karte umdrehen',
        flipCardDesc: 'Kartenvorder- und rückseite umkehren.',
        waitClick: 'Klick abwarten',
        waitClickDesc: 'Auf Benutzerklick warten.',
        delay: 'Verzögerung',
        delayDesc: 'Vor dem Fortfahren pausieren.',
        earlyExit: 'Vorzeitiger Abbruch',
        earlyExitDesc: 'Fluss frühzeitig beenden.',
        playAudio: 'Audio abspielen',
        playAudioDesc: 'Karten-Audio abspielen.',
        action: 'Aktion',
        actionDesc: 'Allgemeine Flussaktion.',
        getValue: 'Wert lesen',
        getValueDesc: 'Quelle → Register.',
        setValue: 'Wert setzen',
        setValueDesc: 'Register → Ziel.',
        math: 'Rechenoperation',
        mathDesc: 'Register-Arithmetik.',
        phase: 'Phase',
        phaseDesc: 'Checkpoint-Markierung.',
        phaseSave: 'Phase speichern',
        phaseSaveDesc: 'Fortschritts-Checkpoint speichern.',
        categoryMedia: 'Medien',
        showMedia: 'Medium anzeigen',
        showMediaDesc: 'Kursmaterial anzeigen (Bild, Video, Audio oder PDF).',
      },
      displayMode: 'Anzeigemodus',
      standalone: 'Eigenständig',
      standaloneDesc: 'Flashcard ausblenden, nur Medium anzeigen.',
      modal: 'Modal',
      modalDesc: 'Schwebendes Fenster über der Flashcard.',
      split: 'Splitscreen',
      splitDesc: 'Zwei Medien nebeneinander.',
      panel: 'Seitenleiste',
      displayModePanelHint: 'Medium öffnet als Seitenleiste neben der Flashcard.',
      splitRightMedia: 'Rechte Seite (Medienname)',
      splitRightMediaPlaceholder: 'Name des Medienelements…',
      splitSwap: 'Links und rechts tauschen',
    },
  },
}
